import { bindClickWheelZoom } from "../utils/clickWheelZoom";
import { useCallback, useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Button, Divider, FormControlLabel, IconButton, Menu, MenuItem, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useIsGreek } from "../context/LanguageContext";
import { obstacleFootprint, roofFootprint } from "../utils/siteGeometry";
import { validPolygon } from "../utils/footprint";
import { canAppendVertex, snapVertex } from "../utils/sitePlanDrawing";
import PlanMapBackground from "./PlanMapBackground";
import GroundRowsLayer from "./GroundRowsLayer";
import { rowFootprint } from "../utils/groundRowEditing";
import { sceneOffset, descriptorRows } from "../utils/projectScene";
import { bearingOf, edgeOutwardAzimuth, longestEdgeAzimuth, polygonCentroid, roundAzimuth } from "../utils/roofAzimuth";
import { facingEdge, fitDrawnRoofFace, fitFaceToNeighbours } from "../utils/roofFaces";
import { compass } from "../utils/vec3";
import { rotatedModuleQuadsToAxis, snapModuleDelta } from "../utils/moduleLayout";
import { outlineMeasurements } from "../utils/outlineMeasurements";
import { moduleClearanceGuides } from "../utils/moduleClearances";
import { canRotateObstacle, obstacleRotationPatch } from "../utils/obstacleRotation";
import { modulesFitRoof, panelTurnAllowed } from "../utils/editModulePlan";

const roundMetres = (value) => Number(value.toFixed(2));
const roofPrecision = (value) => Number(value.toFixed(9));

export default function SitePlanEditor({ groundRows = null, onGroundRowsChange = null, onAlignPanels = null, onRotatePanels = null, panelQuads = [], onEditPanels = null,
  onUndoPanelEdit = null, missingPanels = [], unassignedSlots = [], faceNames = {}, value, onChange, allowRoofFaces = false, mapOrigin = null, roofPitch = 0, roofAzimuth = 180, contextPlanes = [],
  // The active PV face's azimuth belongs to its orientation, not to the scene
  // geometry; setting it from the plan goes back through this.
  onRoofAzimuthChange = null,
  // A face gets modules or loses them: onFacePv({ obstacle }, true) makes a
  // "roof without PV" face a plane of its own, onFacePv({ planeId }, false)
  // makes another plane a face of this scene. onSelectPlane(id) makes another
  // plane the one being edited.
  onFacePv = null, onSelectPlane = null,
  selectedObject = undefined, onSelectionChange = null,
  // Where the plan was looking, in project metres, and the plan image laid
  // over it: kept here so a click on another face (which remounts the plan
  // in that face's own frame) does not move the view or drop the image.
  viewRef = null,
  // Other objects in the common scene, transformed into this plan's frame.
  sharedObstacles = null,
  // Moving or editing one writes back into the common scene geometry.
  onSharedObstacleChange = null, onSharedObstacleMove = null, onSharedObstacleRemove = null }) {
  const greek = useIsGreek(), tr = (el, en) => greek ? el : en;
  const frameEast = Number(value?.frame?.eastM) || 0, frameNorth = Number(value?.frame?.northM) || 0;
  const kept = viewRef?.current?.plan || null;
  const [extent, setExtent] = useState(kept?.extent || 40), [localSelected, setLocalSelected] = useState(null);
  const selected = selectedObject === undefined ? localSelected : selectedObject;
  const setSelected = useCallback((id) => {
    if (selectedObject === undefined) setLocalSelected(id);
    onSelectionChange?.(id);
  }, [selectedObject, onSelectionChange]);
  const [aligning, setAligning] = useState(false);
  const [alignmentDirection, setAlignmentDirection] = useState("across");
  const [rotatingPanels, setRotatingPanels] = useState(false), [panelAxisDraft, setPanelAxisDraft] = useState("");
  const [rotatingObjectId, setRotatingObjectId] = useState(null);
  const [panelMove, setPanelMove] = useState(null);
  const [moveArray, setMoveArray] = useState(false);
  const [panelSnap, setPanelSnap] = useState(kept?.panelSnap ?? true);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedGroundRow, setSelectedGroundRow] = useState(null);
  const [moveGroundGroup, setMoveGroundGroup] = useState(false);
  const [drawing, setDrawing] = useState(false), [points, setPoints] = useState([]);
  const [drawingKind, setDrawingKind] = useState("polygon");
  const svgRef = useRef(null), drag = useRef(null), suppressClick = useRef(false);
  const vertexPreviewRef = useRef(null);
  useEffect(() => {
    if (!panelMove) return undefined;
    const cancel = event => {
      if (event.key !== "Escape") return;
      event.preventDefault(); event.stopPropagation();
      drag.current = null; suppressClick.current = true; setPanelMove(null);
    };
    document.addEventListener("keydown", cancel, true);
    return () => document.removeEventListener("keydown", cancel, true);
  }, [panelMove]);
  const [vertexPreview, setVertexPreview] = useState(null);
  const [outlineEditId, setOutlineEditId] = useState(null);
  useEffect(() => {
    if (!outlineEditId) return undefined;
    const finish = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault(); event.stopPropagation();
      drag.current = null; suppressClick.current = true;
      vertexPreviewRef.current = null; setVertexPreview(null); setOutlineEditId(null);
    };
    document.addEventListener("keydown", finish, true);
    return () => document.removeEventListener("keydown", finish, true);
  }, [outlineEditId]);
  useEffect(() => {
    if (!drawing) return undefined;
    const restart = (event) => {
      if (event.key !== "Escape") return;
      // Consume Escape before the enclosing dialog closes. Keep the tool
      // active, so the next click starts a fresh outline of the same kind.
      event.preventDefault();
      event.stopPropagation();
      drag.current = null;
      suppressClick.current = false;
      setPoints([]); setHover(null); setError("");
    };
    document.addEventListener("keydown", restart, true);
    return () => document.removeEventListener("keydown", restart, true);
  }, [drawing]);
  useEffect(() => {
    if (!aligning && !rotatingPanels && !rotatingObjectId) return undefined;
    const cancel = (event) => {
      const finishObject = event.key === "Enter" && !event.isComposing
        && rotatingObjectId != null && selected === rotatingObjectId;
      if (event.key !== "Escape" && !finishObject) return;
      event.preventDefault(); event.stopPropagation();
      if (drag.current?.objectRotation) { drag.current = null; suppressClick.current = true; }
      setAligning(false); setRotatingPanels(false); setRotatingObjectId(null);
    };
    document.addEventListener("keydown", cancel, true);
    return () => document.removeEventListener("keydown", cancel, true);
  }, [aligning, rotatingPanels, rotatingObjectId, selected]);
  const [width, setWidth] = useState(800), [centre, setCentre] = useState(kept ? { x: kept.x - frameEast, y: kept.y + frameNorth } : { x: 0, y: 0 });
  const [height, setHeight] = useState(560);
  const [hover, setHover] = useState(null), [error, setError] = useState("");
  const [image, setImage] = useState(kept?.image || null), [imageWidth, setImageWidth] = useState(kept?.imageWidth || 40);
  const [imageX, setImageX] = useState(kept ? kept.imageX - frameEast : 0), [imageY, setImageY] = useState(kept ? kept.imageY - frameNorth : 0), [rotation, setRotation] = useState(kept?.rotation || 0);
  const [satellite, setSatellite] = useState(kept ? kept.satellite : true), [tileError, setTileError] = useState(false);
  useEffect(() => {
    if (!viewRef) return;
    // The SVG y axis points south (y = -north), so the centre's north
    // component converts with the opposite sign to the image's, which is
    // kept north-up.
    viewRef.current = { ...viewRef.current, plan: { extent, x: centre.x + frameEast, y: centre.y - frameNorth,
      image, imageWidth, imageX: imageX + frameEast, imageY: imageY + frameNorth, rotation, satellite, panelSnap } };
  }, [viewRef, extent, centre, frameEast, frameNorth, image, imageWidth, imageX, imageY, rotation, satellite, panelSnap]);
  // The plan is drawn in this face's frame. When the frame moves (the roof
  // a face given PV becoming this plane's roof, the frame typed),
  // the view and the plan image move the other way, so they stay where they
  // were over the ground and only this face's own things move.
  const lastFrame = useRef({ x: frameEast, y: frameNorth });
  useEffect(() => {
    const dx = frameEast - lastFrame.current.x, dy = frameNorth - lastFrame.current.y;
    lastFrame.current = { x: frameEast, y: frameNorth };
    if (!dx && !dy) return;
    setCentre((c) => ({ x: c.x - dx, y: c.y + dy }));
    setImageX((v) => v - dx); setImageY((v) => v - dy);
  }, [frameEast, frameNorth]);
  const viewHeight = extent * height / width;
  const bounds = { x: centre.x - extent / 2, y: centre.y - viewHeight / 2, width: extent, height: viewHeight };
  useEffect(() => {
    const node = svgRef.current;
    if (!node) return;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setWidth(rect.width || 800);
      setHeight(rect.height || 560);
    };
    update();
    window.addEventListener("resize", update);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(node);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); };
  }, []);
  const zoom = (factor) => setExtent((current) => Math.max(2, Math.min(2000, current * factor)));
  useEffect(() => {
    const node = svgRef.current;
    if (!node) return undefined;
    let wheelActive = false;
    const releaseWheel = bindClickWheelZoom(node, (active) => { wheelActive = active; });
    const wheel = (event) => { if (!wheelActive) return; event.preventDefault(); zoom(event.deltaY > 0 ? 1.15 : 1 / 1.15); };
    node?.addEventListener("wheel", wheel, { passive: false });
    return () => { node.removeEventListener("wheel", wheel); releaseWheel(); };
  }, []);
  const obstacles = value.obstacles || [];
  const rotatingShared = (sharedObstacles || []).find(x => `shared:${x.planeId}:${x.obstacle.id}` === rotatingObjectId);
  const rotatingObject = rotatingShared?.obstacle || obstacles.find(o => o.id === rotatingObjectId);
  const objectRotationVisible = !drawing && selected === rotatingObjectId && canRotateObstacle(rotatingObject);
  const objectRadius = objectRotationVisible ? Math.max(extent / width * 48,
    ...obstacleFootprint(rotatingObject).map(p => Math.hypot(p.x - Number(rotatingObject.eastM), p.y - Number(rotatingObject.northM)) + extent / width * 24)) : 0;
  const objectBearing = Number(rotatingObject?.rotationDeg) || 0;
  const objectHandle = objectRotationVisible ? {
    x: Number(rotatingObject.eastM) + Math.sin(objectBearing * Math.PI / 180) * objectRadius,
    y: -(Number(rotatingObject.northM) + Math.cos(objectBearing * Math.PI / 180) * objectRadius),
  } : null;
  const showObjectRotation = (id) => {
    finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(id); setSelected(id); setMenu(null);
  };
  const roofPoints = vertexPreview?.id === "roof" ? vertexPreview.ring : value.roof ? roofFootprint(value.roof) : [];
  const contextGroundGroups = groundRows ? (contextPlanes || []).filter(p => !p.onRoof).flatMap(plane => {
    const offset = sceneOffset({ siteGeometry: value, origin: mapOrigin }, plane);
    if (!offset) return [];
    return [{ id: plane.id, label: plane.label, rows: descriptorRows(plane).map(row =>
      [row.low[0], row.low[1], row.high[1], row.high[0]].map(p => ({ x: p.x + offset.x, y: p.y + offset.y }))) }];
  }) : [];
  const contextOutlines = (contextPlanes || []).flatMap((plane) => {
    const offset = sceneOffset({ siteGeometry: value }, plane);
    if (!offset) return [];
    const sources = [plane.siteGeometry?.roof || null,
      ...(plane.siteGeometry?.obstacles || []).filter((o) =>
        !(sharedObstacles || []).some((item) => item.planeId === plane.id && item.obstacle.id === o.id))];
    const rings = sources.map((s, i) => (i === 0 ? (s ? roofFootprint(s) : []) : obstacleFootprint(s)));
    return rings.map((ring, i) => ({ ring, i })).filter(({ ring }) => ring.length).map(({ ring, i }) => ({ id: `${plane.id}:${i}`, planeId: plane.id, label: plane.label,
      roof: i === 0 && Boolean(plane.siteGeometry?.roof), primary: Boolean(plane.primary), offset,
      eaveEdge: i === 0 || sources[i]?.kind === "roof" ? sources[i]?.eaveEdge : null,
      roofSurface: i === 0 ? Boolean(sources[i]) : sources[i]?.kind === "roof",
      pitchDeg: sources[i]?.pitchDeg, azimuthDeg: i === 0 ? sources[i]?.azimuthDeg : sources[i]?.rotationDeg,
      surfaceOrigin: { x: offset.x + (i === 0 ? 0 : Number(sources[i]?.eastM) || 0),
        y: offset.y + (i === 0 ? 0 : Number(sources[i]?.northM) || 0) },
      surfaceHeight: i === 0 ? offset.z : offset.z - (Number(plane.siteGeometry?.roof?.heightM) || 0)
        + (Number(sources[i]?.baseHeightM) || 0) + Number(sources[i]?.heightM),
      points: ring.map((p) => ({ x: p.x + offset.x, y: p.y + offset.y })) }));
  });
  // Every roof surface a new face can be drawn against, in this plan's frame
  // with z from this scene's datum (utils/roofFaces): the active PV face, the
  // other planes' faces and the faces without PV. A face's own height above
  // the ground is heightM; the datum is the active roof's centre when there
  // is one, the ground otherwise.
  const groundZ = value.roof && Number.isFinite(Number(value.roof.heightM)) && value.roof.heightM !== "" ? -Number(value.roof.heightM) : 0;
  const faceSurfaces = (exceptId = null) => [
    ...(exceptId !== "roof" && value.roof && roofPoints.length >= 3 ? [{ id: "roof", ring: roofPoints, origin: { x: 0, y: 0 },
      pitchDeg: value.roof.pitchDeg ?? roofPitch, azimuthDeg: value.roof.azimuthDeg ?? roofAzimuth, zAtOrigin: 0 }] : []),
    ...contextOutlines.filter((o) => o.roofSurface).map((o) => ({ id: `plane:${o.id}`, ring: o.points,
      origin: o.surfaceOrigin, pitchDeg: o.pitchDeg, azimuthDeg: o.azimuthDeg, zAtOrigin: o.surfaceHeight })),
    ...obstacles.filter((o) => o.kind === "roof" && o.id !== exceptId).map((o) => ({ id: o.id, ring: obstacleFootprint(o),
      origin: { x: Number(o.eastM), y: Number(o.northM) }, pitchDeg: o.pitchDeg, azimuthDeg: o.rotationDeg,
      zAtOrigin: groundZ + (Number(o.baseHeightM) || 0) + Number(o.heightM) })),
    ...(sharedObstacles || []).filter(({ planeId, obstacle: o }) => o.kind === "roof" && `shared:${planeId}:${o.id}` !== exceptId).map(({ planeId, obstacle: o, sceneGroundZM = groundZ }) => ({
      id: `shared:${planeId}:${o.id}`, ring: obstacleFootprint(o), origin: { x: Number(o.eastM), y: Number(o.northM) },
      pitchDeg: o.pitchDeg, azimuthDeg: o.rotationDeg,
      zAtOrigin: sceneGroundZM + (Number(o.baseHeightM) || 0) + Number(o.heightM),
    })),
  ];
  // The stored height of a face whose surface stands at z at its own centre.
  const faceHeightFor = (zAtOrigin, baseHeightM = 0) => roofPrecision(zAtOrigin - groundZ - (Number(baseHeightM) || 0));
  // Every roof face with a "PV" badge: this plane's, the other planes', and
  // the faces without modules. The badge is where modules are given or taken.
  const pvBadges = !drawing && onFacePv ? [
    ...(value.roof && roofPoints.length >= 3 ? [{ key: "roof", ring: roofPoints, pv: true, active: true }] : []),
    ...contextOutlines.filter((o) => o.roof).map((o) => ({ key: `plane:${o.planeId}`, ring: o.points, pv: true, planeId: o.planeId, primary: o.primary, label: o.label })),
    ...obstacles.filter((o) => o.kind === "roof").map((o) => ({ key: `face:${o.id}`, ring: obstacleFootprint(o), pv: false, obstacle: o })),
  ].map((badge) => ({ ...badge, centre: polygonCentroid(badge.ring) })).filter((badge) => badge.centre) : [];
  const badgeAction = (key) => {
    const badge = pvBadges.find((b) => b.key === key);
    if (!badge) return;
    if (badge.obstacle) onFacePv({ obstacle: badge.obstacle }, true);
    else if (badge.planeId && !badge.primary) onFacePv({ planeId: badge.planeId }, false);
  };
  const groundBoundary = (groundRows?.layout?.outline?.boundary_xy || []).map(([x, y]) => ({ x, y }));
  const groundVertices = (groundRows?.layout?.tables || []).flatMap(t => rowFootprint(t, groundRows.depth));
  const vertices = [...groundBoundary, ...groundVertices, ...roofPoints, ...obstacles.flatMap((o) => obstacleFootprint(o)), ...contextOutlines.flatMap((o) => o.points),
    ...contextGroundGroups.flatMap(group => group.rows.flat()),
    ...(sharedObstacles || []).flatMap(({ obstacle }) => obstacleFootprint(obstacle))];
  // Every roof face with a slope to point: the active PV face and the faces
  // without PV. An edge click chooses the eave; dragging the arrow sets a
  // free downhill bearing while keeping that edge as the height reference.
  const [hoverEdge, setHoverEdge] = useState(null);
  // The arrow follows the pointer from here while it is dragged; the value is
  // written once, when the pointer is released, so undo is one step.
  const [azimuthPreview, setAzimuthPreview] = useState(null);
  // The slope arrow is shown on demand, from the right-click menu of a face,
  // so the plan stays clean; the menu also deletes an object.
  const [arrowsShown, setArrowsShown] = useState(() => new Set());
  const [menu, setMenu] = useState(null);                // { x, y, target }
  const toggleArrow = (id) => {
    finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null);
    setArrowsShown((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  // The eave of a face: as stored, or (a rectangle given by its bearing, or a
  // face not yet resolved) the edge facing its azimuth.
  const eaveOf = (ring, stored, azimuth) => (Number.isInteger(stored) && stored >= 0 && stored < ring.length
    ? stored : facingEdge(ring, azimuth));
  const allRoofFaces = [
    ...(value.roof && roofPoints.length >= 3
      ? [{ id: "roof", ring: roofPoints, azimuth: Number(value.roof.azimuthDeg ?? roofAzimuth), editable: true, pv: true,
          freeAzimuth: value.roof.freeAzimuth, eave: value.roof.footprint ? value.roof.eaveEdge : null,
          label: tr("Επιλεγμένη πλευρά με Φ/Β", "Selected PV face") }] : []),
    ...obstacles.filter((o) => o.kind === "roof").map((o) => ({ id: o.id,
      ring: vertexPreview?.id === o.id ? vertexPreview.ring : obstacleFootprint(o), azimuth: Number(o.rotationDeg) || 0,
      editable: true, freeAzimuth: o.freeAzimuth, eave: o.footprint ? o.eaveEdge : null,
      label: o.name || tr("Πλευρά στέγης", "Roof face") })),
    ...(sharedObstacles || []).filter(({ obstacle: o }) => o.kind === "roof").map(({ planeId, obstacle: o }) => ({
      id: `shared:${planeId}:${o.id}`,
      ring: vertexPreview?.id === `shared:${planeId}:${o.id}` ? vertexPreview.ring : obstacleFootprint(o),
      azimuth: Number(o.rotationDeg) || 0, editable: true, freeAzimuth: o.freeAzimuth,
      eave: o.footprint ? o.eaveEdge : null, label: o.name || tr("Πλευρά στέγης", "Roof face"),
    })),
  ].filter((face) => face.ring.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))).map((face) => {
    const azimuth = vertexPreview?.id === face.id ? vertexPreview.azimuth : face.azimuth;
    const centre = polygonCentroid(face.ring);
    const xs = face.ring.map((p) => p.x), ys = face.ring.map((p) => p.y);
    const size = Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    return { ...face, azimuth, eave: eaveOf(face.ring, face.eave, azimuth), centre, length: Math.max(0.8, size * 0.22) };
  }).filter((face) => face.centre);
  const roofFaces = allRoofFaces.filter((face) => arrowsShown.has(face.id));
  const outlineFace = allRoofFaces.find((face) => face.id === outlineEditId);
  // A deleted/switched face must not leave invisible edit handles active.
  const outlineExists = Boolean(outlineFace);
  useEffect(() => { if (!outlineExists) setOutlineEditId(null); }, [outlineExists]);
  const selectedContextRoof = contextOutlines.find((o) => o.roof && `plane:${o.planeId}` === selected);
  const selectedRoofFace = allRoofFaces.find((face) => face.id === selected)
    || (selectedContextRoof ? { centre: polygonCentroid(selectedContextRoof.points), label: selectedContextRoof.label } : null);
  const selectedRoofName = selectedRoofFace ? faceNames[selected] || selectedRoofFace.label : null;
  const panelAxis = panelQuads.length ? (() => {
    const q = panelQuads[0], dx = q.high[0].x - q.low[0].x, dy = q.high[0].y - q.low[0].y;
    return Math.round((((Math.atan2(dx, dy) * 180 / Math.PI) % 360) + 360) % 360 * 10) / 10;
  })() : null;
  const panelCentre = panelQuads.length ? panelQuads.reduce((sum, q) => ({
    x: sum.x + (q.low[0].x + q.low[1].x + q.high[0].x + q.high[1].x) / (4 * panelQuads.length),
    y: sum.y + (q.low[0].y + q.low[1].y + q.high[0].y + q.high[1].y) / (4 * panelQuads.length),
  }), { x: 0, y: 0 }) : null;
  const previewBearing = Number(panelAxisDraft);
  const proposedTurn = rotatingPanels && panelAxisDraft !== "" && Number.isFinite(previewBearing)
    && previewBearing >= 0 && previewBearing <= 360
    ? rotatedModuleQuadsToAxis(panelQuads, previewBearing % 360) : panelQuads;
  const invalidTurn = rotatingPanels && !panelTurnAllowed(proposedTurn, value);
  const turnedPanels = invalidTurn ? panelQuads : proposedTurn;
  const turnError = tr("Η περιστροφή δεν χωρά στην πλευρά ή προκαλεί σύγκρουση με τη στέγη, άλλο πάνελ ή εμπόδιο. Δοκιμάστε άλλη γωνία ή μετακινήστε τη συστοιχία.",
    "The rotation exceeds the roof boundary or collides with the roof, another module or an obstacle. Try another angle or move the array.");
  const commitPanelTurn = (bearing) => {
    if (!panelTurnAllowed(rotatedModuleQuadsToAxis(panelQuads, bearing), value)
      || onRotatePanels?.(bearing) === false) {
      setError(turnError);
      return false;
    }
    setError("");
    return true;
  };
  const previewPanels = panelMove ? turnedPanels.map(q => {
    if (!panelMove.all && q.slot !== panelMove.slot) return q;
    const move = p => ({ ...p, x: p.x + panelMove.delta.x, y: p.y + panelMove.delta.y });
    return { ...q, low: q.low.map(move), high: q.high.map(move) };
  }) : turnedPanels;
  const panelClearances = panelMove ? moduleClearanceGuides({
    selected: previewPanels.filter(q => panelMove.all || q.slot === panelMove.slot).map(q => ({ ...q, id: q.slot })),
    modules: previewPanels.map(q => ({ ...q, id: q.slot })),
    roof: value?.roof ? roofFootprint(value.roof) : [],
    obstacles: [...obstacles.map(o => ({ obstacle: o, planeId: "active" })), ...(sharedObstacles || [])]
      .filter(({ obstacle: o }) => o.kind !== "roof")
      .map(({ obstacle: o, planeId }) => ({ id: `${planeId}:${o.id}`, label: o.name || tr("Εμπόδιο", "Obstacle"),
        ring: o.kind === "tree" ? Array.from({ length: 16 }, (_, i) => ({
          x: Number(o.eastM) + Number(o.widthM) / 2 * Math.cos(i * Math.PI / 8),
          y: Number(o.northM) + Number(o.depthM) / 2 * Math.sin(i * Math.PI / 8),
        })) : obstacleFootprint(o) })),
  }) : [];
  const rotationRadius = panelCentre ? Math.max(extent / width * 48,
    Math.min(extent / width * 100, panelQuads.reduce((farthest, q) => Math.max(farthest,
      ...[q.low[0], q.low[1], q.high[0], q.high[1]].map((p) => Math.hypot(p.x - panelCentre.x, p.y - panelCentre.y))), 0)
      + extent / width * 22)) : 0;
  const shownBearing = panelAxisDraft !== "" && Number.isFinite(previewBearing) && previewBearing >= 0 && previewBearing <= 360
    ? previewBearing : panelAxis;
  const handleX = panelCentre ? panelCentre.x + Math.sin(shownBearing * Math.PI / 180) * rotationRadius : 0;
  const handleY = panelCentre ? -(panelCentre.y + Math.cos(shownBearing * Math.PI / 180) * rotationRadius) : 0;
  const showPanelRotation = () => {
    finishOutline(); setRotatingObjectId(null); setPanelAxisDraft(String(panelAxis ?? 0)); setRotatingPanels(true); setAligning(false);
  };
  const isSharedVertex = (face, point) => [...allRoofFaces.filter((other) => other.id !== face.id).map((other) => other.ring),
    ...contextOutlines.filter((other) => other.roof).map((other) => other.points)]
    .some((ring) => ring.some((a, i) => {
      const b = ring[(i + 1) % ring.length];
      const dx = b.x - a.x, dy = b.y - a.y, length2 = dx * dx + dy * dy;
      const t = length2 ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / length2)) : 0;
      return Math.hypot(point.x - a.x - t * dx, point.y - a.y - t * dy) < 0.01;
    }));
  const previewVertex = (face, index, point) => {
    const ring = face.ring.map((p, i) => i === index ? point : p);
    const bearing = face.freeAzimuth ? face.azimuth : edgeOutwardAzimuth(ring, face.eave);
    const azimuth = bearing == null ? face.azimuth : Math.round(bearing * 10) / 10;
    const preview = { id: face.id, ring, azimuth, eave: face.eave, freeAzimuth: face.freeAzimuth };
    vertexPreviewRef.current = preview;
    setVertexPreview(preview);
  };
  const clearVertexPreview = () => { vertexPreviewRef.current = null; setVertexPreview(null); };
  const finishOutline = () => { drag.current = null; clearVertexPreview(); setOutlineEditId(null); };
  const editOutline = (id) => {
    clearVertexPreview(); setError(""); setMenu(null); setAligning(false); setRotatingPanels(false);
    setArrowsShown(new Set()); setSelected(id); setOutlineEditId(id === outlineEditId ? null : id);
  };
  const commitVertexPreview = () => {
    const preview = vertexPreviewRef.current;
    clearVertexPreview();
    if (!preview || !validPolygon(preview.ring)) {
      setError(tr("Η μετακίνηση θα δημιουργούσε μη έγκυρο περίγραμμα. Η κορυφή έμεινε στη θέση της.",
        "The move would create an invalid outline. The corner stayed in place."));
      return;
    }
    const xs = preview.ring.map((p) => p.x), ys = preview.ring.map((p) => p.y);
    const size = { widthM: roundMetres(Math.max(...xs) - Math.min(...xs)),
      depthM: roundMetres(Math.max(...ys) - Math.min(...ys)) };
    if (preview.id === "roof") {
      onChange({ ...value, roof: { ...value.roof, ...size, footprint: preview.ring,
        eaveEdge: preview.eave, ...(!preview.freeAzimuth ? { azimuthDeg: preview.azimuth } : {}) } });
      if (!preview.freeAzimuth) onRoofAzimuthChange?.(preview.azimuth);
      return;
    }
    if (preview.id.startsWith("shared:")) {
      const shared = (sharedObstacles || []).find(({ planeId, obstacle }) =>
        `shared:${planeId}:${obstacle.id}` === preview.id);
      if (!shared) return;
      const o = shared.obstacle;
      onSharedObstacleChange?.(shared.planeId, o.id, { ...size,
        footprint: preview.ring.map((p) => ({ x: roundMetres(p.x - Number(o.eastM)), y: roundMetres(p.y - Number(o.northM)) })),
        eaveEdge: preview.eave, ...(!preview.freeAzimuth ? { rotationDeg: preview.azimuth } : {}) });
      return;
    }
    onChange({ ...value, obstacles: obstacles.map((o) => o.id === preview.id ? { ...o, ...size,
      footprint: preview.ring.map((p) => ({ x: roundMetres(p.x - Number(o.eastM)), y: roundMetres(p.y - Number(o.northM)) })),
      eaveEdge: preview.eave, ...(!preview.freeAzimuth ? { rotationDeg: preview.azimuth } : {}) } : o) });
  };
  // The eave of a face, from the plan. A face given by width, depth and
  // bearing keeps its outline from here on, so the new facing does not turn
  // the rectangle; the azimuth and the height then follow from the eave
  // (PhysicalLayoutDialog's roof model).
  const setFaceEave = (faceId, index) => {
    const face = allRoofFaces.find((f) => f.id === faceId);
    if (!face || !Number.isInteger(index)) return;
    const deg = roundAzimuth(edgeOutwardAzimuth(face.ring, index));
    if (faceId === "roof") {
      onChange({ ...value, roof: { ...value.roof, footprint: value.roof.footprint || roofPoints.map((p) => ({ x: p.x, y: p.y })), eaveEdge: index, freeAzimuth: false, azimuthSource: "eave" } });
      if (deg != null) onRoofAzimuthChange?.(deg);
      return;
    }
    if (faceId.startsWith("shared:")) {
      const sharedFace = (sharedObstacles || []).find(({ planeId, obstacle }) => `shared:${planeId}:${obstacle.id}` === faceId);
      if (!sharedFace) return;
      const o = sharedFace.obstacle;
      const footprint = o.footprint || obstacleFootprint(o).map((p) => ({ x: roundMetres(p.x - Number(o.eastM)), y: roundMetres(p.y - Number(o.northM)) }));
      onSharedObstacleChange?.(sharedFace.planeId, o.id,
        { footprint, eaveEdge: index, freeAzimuth: false, azimuthSource: "eave", ...(deg != null ? { rotationDeg: deg } : {}) });
      return;
    }
    onChange({ ...value, obstacles: obstacles.map((o) => {
      if (o.id !== faceId) return o;
      const footprint = o.footprint || obstacleFootprint(o).map((p) => ({ x: roundMetres(p.x - Number(o.eastM)), y: roundMetres(p.y - Number(o.northM)) }));
      const { userAngles, independentHeight, ...rest } = o;
      return { ...rest, footprint, eaveEdge: index, freeAzimuth: false, azimuthSource: "eave", ...(deg != null ? { rotationDeg: deg } : {}) };
    }) });
  };
  const setFaceAzimuth = (faceId, degrees) => {
    const azimuthDeg = roundAzimuth(degrees);
    const face = allRoofFaces.find((f) => f.id === faceId);
    if (azimuthDeg == null || !face) return;
    if (faceId === "roof") {
      onChange({ ...value, roof: { ...value.roof, footprint: value.roof.footprint || roofPoints.map((p) => ({ x: p.x, y: p.y })),
        eaveEdge: face.eave, freeAzimuth: true, azimuthSource: "manual", azimuthDeg } });
      onRoofAzimuthChange?.(azimuthDeg);
      return;
    }
    if (faceId.startsWith("shared:")) {
      const sharedFace = (sharedObstacles || []).find(({ planeId, obstacle }) => `shared:${planeId}:${obstacle.id}` === faceId);
      if (!sharedFace) return;
      const o = sharedFace.obstacle;
      const footprint = o.footprint || obstacleFootprint(o).map((p) => ({ x: roundMetres(p.x - Number(o.eastM)), y: roundMetres(p.y - Number(o.northM)) }));
      onSharedObstacleChange?.(sharedFace.planeId, o.id, { footprint, eaveEdge: face.eave, freeAzimuth: true, azimuthSource: "manual", rotationDeg: azimuthDeg });
      return;
    }
    onChange({ ...value, obstacles: obstacles.map((o) => o.id === faceId
      ? { ...o, footprint: o.footprint || obstacleFootprint(o).map((p) => ({ x: roundMetres(p.x - Number(o.eastM)), y: roundMetres(p.y - Number(o.northM)) })),
        eaveEdge: face.eave, freeAzimuth: true, azimuthSource: "manual", rotationDeg: azimuthDeg } : o) });
  };
  const fromEvent = (event) => {
    const svg = svgRef.current, p = svg.createSVGPoint(); p.x = event.clientX; p.y = event.clientY;
    const xy = p.matrixTransform(svg.getScreenCTM().inverse());
    return { x: Math.round(xy.x * 100) / 100, y: -Math.round(xy.y * 100) / 100 };
  };
  const snap = (point) => snapVertex(point, [...(points.length >= 3 ? [points[0]] : []), ...vertices], extent / width * 12);
  const fitPoints = (candidates, parcel = false) => {
    const all = candidates.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    if (!all.length) { setExtent(40); setCentre({ x: 0, y: 0 }); return; }
    const xs = all.map((p) => p.x), ys = all.map((p) => -p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    setCentre({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 });
    const fitted = Math.max(maxX - minX, (maxY - minY) * width / height) * 1.3;
    setExtent(Math.max(5, parcel ? fitted : Math.min(2000, fitted)));
  };
  const fit = () => fitPoints([...vertices, ...points]);
  const groundFitted = useRef(null);
  useEffect(() => {
    if (!groundRows) return;
    // Start with the parcel, even before rows exist. Distant obstacles must
    // not zoom it out; moving/resizing rows must not reset a user's view.
    const parcelKey = groundBoundary.length > 2 ? JSON.stringify(groundBoundary) : null;
    const key = parcelKey ? `${parcelKey}:${width}:${height}` : (groundRows.layout.tables.length ? "rows" : null);
    if (!key || groundFitted.current === key) return;
    groundFitted.current = key;
    if (parcelKey) fitPoints(groundBoundary, true);
    else if (!kept) fit();
  });
  const upload = async (event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setError(tr("Επιλέξτε PNG, JPG ή WebP έως 10 MB.", "Choose PNG, JPG or WebP up to 10 MB.")); return;
    }
    const url = URL.createObjectURL(file), img = new Image();
    img.onload = () => {
      if (image) URL.revokeObjectURL(image.url);
      setImage({ url, ratio: img.naturalHeight / img.naturalWidth, name: file.name });
      setImageWidth(extent); setImageX(centre.x); setImageY(-centre.y); setRotation(0); setSatellite(false); setError("");
    };
    img.onerror = () => { URL.revokeObjectURL(url); setError(tr("Η εικόνα δεν μπορεί να διαβαστεί.", "The image could not be read.")); };
    img.src = url;
  };
  useEffect(() => () => { if (image) URL.revokeObjectURL(image.url); }, [image]);
  const showMap = satellite && mapOrigin?.length === 2 && mapOrigin.every(Number.isFinite) && Math.abs(mapOrigin[1]) < 85;
  const path = (ring) => ring.map((p) => `${p.x},${-p.y}`).join(" ");
  const commit = () => {
    if (!validPolygon(points)) { setError(tr("Το κλείσιμο δημιουργεί τεμνόμενες ακμές ή μη έγκυρο περίγραμμα.", "Closing creates crossing edges or an invalid footprint.")); return; }
    const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
    if (drawingKind === "pv-roof") {
      const joined = fitDrawnRoofFace(points, { x: 0, y: 0 }, faceSurfaces("roof"), Number(value.roof?.pitchDeg ?? roofPitch));
      if (joined.status === "conflict") {
        setError(tr("Η νέα πλευρά δεν μπορεί να ενωθεί χωρίς σκαλί με όλες τις γειτονικές ακμές. Ελέγξτε τις κοινές κορυφές και τα ύψη.",
          "The new face cannot meet every neighbouring edge without a step. Check the shared corners and heights."));
        return;
      }
      const fitted = joined.status === "fitted" ? { pitchDeg: joined.face.pitchDeg, azimuthDeg: joined.face.azimuthDeg,
        heightM: faceHeightFor(joined.face.zAtOrigin), eaveEdge: joined.face.eaveEdge,
        eaveHeightM: roofPrecision(joined.face.eaveHeightM - groundZ), freeAzimuth: Boolean(joined.face.freeAzimuth),
        azimuthSource: "auto" } : { azimuthSource: "eave" };
      if (joined.status === "fitted" && !(fitted.heightM > 0)) {
        setError(tr("Η προσαρμοσμένη πλευρά βρέθηκε κάτω από τη στάθμη εδάφους. Ελέγξτε τα ύψη των γειτονικών πλευρών.",
          "The fitted roof face would lie below the ground datum. Check the neighbouring face heights."));
        return;
      }
      onChange({ ...value, version: 2,
        frame: value.frame || { eastM: 0, northM: 0, elevationM: 0 },
        roof: { heightM: "", setbackM: 0.5, pitchDeg: roofPitch, azimuthDeg: roofAzimuth,
          ...value.roof, ...fitted, footprint: points.map((p) => ({ ...p })),
          widthM: roundMetres(Math.max(...xs) - Math.min(...xs)), depthM: roundMetres(Math.max(...ys) - Math.min(...ys)) } });
      setPoints([]); setDrawing(false); setSelected(null); setHover(null); setError("");
      return;
    }
    const eastM = (Math.max(...xs) + Math.min(...xs)) / 2;
    const northM = (Math.max(...ys) + Math.min(...ys)) / 2;
    const id = `ob-${Date.now()}-${obstacles.length}`;
    const footprint = points.map((p) => ({ x: p.x - eastM, y: p.y - northM }));
    const size = { widthM: roundMetres(Math.max(...xs) - Math.min(...xs)), depthM: roundMetres(Math.max(...ys) - Math.min(...ys)) };
    // Use the neighbouring face to locate a shared edge. After choosing the
    // new face's outer eave, fit its pitch to that edge when possible.
    const fit = drawingKind === "roof"
      ? fitFaceToNeighbours(points, { x: eastM, y: northM }, faceSurfaces()) : null;
    // Unconnected faces keep the roof's existing pitch and eave height.
    const known = (v) => v !== "" && v != null && Number.isFinite(Number(v));
    const roofObstacles = obstacles.filter((o) => o.kind === "roof");
    const commonEave = [value.roof?.eaveHeightM, ...roofObstacles.map((o) => o.eaveHeightM),
      ...(contextPlanes || []).map((p) => p.siteGeometry?.roof?.eaveHeightM)].find(known);
    const commonPitch = [fit?.pitchDeg, value.roof ? value.roof.pitchDeg ?? roofPitch : null, ...roofObstacles.map((o) => o.pitchDeg), roofPitch].find(known);
    const roofDefaults = { pitchDeg: known(commonPitch) ? Number(commonPitch) : 0, ...(known(commonEave) ? { eaveHeightM: Number(commonEave) } : {}) };
    const joined = drawingKind === "roof"
      ? fitDrawnRoofFace(points, { x: eastM, y: northM }, faceSurfaces(), roofDefaults.pitchDeg) : null;
    if (joined?.status === "conflict") {
      setError(tr("Οι γειτονικές πλευρές δίνουν διαφορετικά ύψη στις κοινές ακμές. Ελέγξτε τις κοινές κορυφές και τις κλίσεις· η νέα πλευρά δεν αποθηκεύτηκε με σκαλί.",
        "Neighbouring faces give incompatible heights along the shared edges. Check their common corners and pitches; the new face was not saved with a step."));
      return;
    }
    if (joined?.status === "fitted" && !(faceHeightFor(joined.face.zAtOrigin) > 0)) {
      setError(tr("Η προσαρμοσμένη πλευρά βρέθηκε κάτω από τη στάθμη εδάφους. Ελέγξτε τα ύψη των γειτονικών πλευρών.",
        "The fitted roof face would lie below the ground datum. Check the neighbouring face heights."));
      return;
    }
    const fitted = joined?.status === "fitted"
      ? { eaveEdge: joined.face.eaveEdge, eaveHeightM: roofPrecision(joined.face.eaveHeightM - groundZ),
        pitchDeg: joined.face.pitchDeg, rotationDeg: joined.face.azimuthDeg,
        heightM: faceHeightFor(joined.face.zAtOrigin),
        azimuthSource: "auto",
        ...(joined.face.freeAzimuth ? { freeAzimuth: true } : {}) }
      : null;
    // Every face is drawn the same way; whether it carries modules is ticked
    // after, in the table or on its badge.
    onChange({ ...value, version: 2, obstacles: [...obstacles, { id,
      name: drawingKind === "roof" ? "" : tr("Πολυγωνικό εμπόδιο", "Polygon obstacle"),
      kind: drawingKind, footprint, eastM, northM, base: "street", baseHeightM: 0, ...size,
      heightM: drawingKind === "roof" ? Number(value.roof?.heightM) || 5 : 3,
      rotationDeg: drawingKind === "roof" ? longestEdgeAzimuth(points, Number(roofAzimuth)) ?? 0 : 0,
      ...(drawingKind === "roof" ? { ...roofDefaults, ...fitted } : {}) }] });
    setPoints([]); setDrawing(false); setSelected(null); setHover(null); setError("");
  };
  const startDrawing = (kind) => { finishOutline(); setDrawing(true); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null); setError(""); setDrawingKind(kind); setPoints([]); setSelected(null); setMenu(null); };
  return <Box sx={{ my: 1.5,
    // Tool buttons, not actions: small, mixed case, one line.
    "& .MuiButton-root": { minHeight: 26, py: 0.2, px: 0.9, fontSize: 12.5, textTransform: "none" },
    "& .MuiButton-startIcon": { mr: 0.4, ml: 0, "& > *:nth-of-type(1)": { fontSize: 15 } } }}>
    <Stack direction="row" gap={0.8} flexWrap="wrap" alignItems="center" sx={{ mb: 0.6 }}>
      <Typography fontWeight={600}>{groundRows ? tr("Κάτοψη γεωτεμαχίου και σειρών", "Parcel and rows plan") : tr("Κάτοψη στέγης και εμποδίων", "Roof and obstacle plan")}</Typography>
      <Tooltip arrow placement="bottom" title={<Stack spacing={0.5} sx={{ maxWidth: 360, p: 0.4 }}>
        <Typography variant="body2">{tr("Δεξί κλικ σε πλευρά, πάνελ ή εμπόδιο για τις διαθέσιμες ενέργειες. Η έντονη ακμή της στέγης είναι το γείσο.",
          "Right-click a face, module or obstacle for its actions. The bold roof edge is the eave.")}</Typography>
        <Typography variant="body2">{tr("Με ορατό το βέλος κλίσης, κλικ σε ακμή αλλάζει το γείσο και σύρσιμο του βέλους αλλάζει τη φορά. Στην «Επεξεργασία περιγράμματος» σύρετε κορυφές και δείτε ζωντανά μήκη και γωνίες κάτοψης. Οι κοινές κορυφές παραμένουν κλειδωμένες.",
          "With the slope arrow visible, click an edge to change the eave or drag the arrow to change the bearing. In Edit outline, drag corners to see live plan lengths and angles. Shared corners remain locked.")}</Typography>
        <Typography variant="body2">{tr("Μια νέα πλευρά προσαρμόζει κλίση, φορά και ύψος για να συναντά τις γειτονικές σε όλες τις κοινές ακμές, χωρίς σκαλί.",
          "A new face adjusts its pitch, facing and height to meet its neighbours along every shared edge without a step.")}</Typography>
        <Typography variant="body2">{tr("Ροδέλα για ζουμ, σύρσιμο στο κενό για μετακίνηση της προβολής, Esc για ακύρωση σχεδίασης. Το δορυφορικό υπόβαθρο δεν είναι τοπογραφική αποτύπωση.",
          "Use the wheel to zoom, drag empty space to pan, and Esc to cancel drawing. Satellite imagery is not a survey.")}</Typography>
      </Stack>}>
        <IconButton size="small" aria-label={tr("Οδηγίες κάτοψης", "Plan instructions")} sx={{ p: 0.2, color: "text.secondary" }}>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      {/* One way to draw a face; PV is ticked on it afterwards. Redrawing the
          open face's outline is on its right-click menu. */}
      {allowRoofFaces && !drawing && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => startDrawing("roof")}>
        {tr("Πλευρά στέγης", "Roof face")}</Button>}
      {(allowRoofFaces || onEditPanels) && !drawing && <Tooltip title={tr("Προσθήκη πάνελ στην ενεργή πλευρά· σύρετέ το για να αλλάξετε θέση.",
        "Add a module to the active face; drag it to change its position.")}>
        <span><Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={!onEditPanels}
          onClick={() => {
            finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null); setMenu(null);
            const result = onEditPanels({ type: "add" });
            setSelected("roof"); setSelectedPanel(result?.slot || null);
          }}>{tr("Πάνελ", "Panel")}</Button></span>
      </Tooltip>}
      {onEditPanels && !drawing && <>
        <Button size="small" variant={!moveArray ? "contained" : "outlined"} aria-pressed={!moveArray}
          onClick={() => {
            finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null);
            setMoveArray(false);
          }}>{tr("Μεμονωμένο πάνελ", "Individual module")}</Button>
        <Button size="small" variant={moveArray ? "contained" : "outlined"} aria-pressed={moveArray}
          disabled={!panelQuads.length} onClick={() => {
            finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null);
            setMoveArray(true); setSelectedPanel(null);
          }}>{tr("Μετακίνηση συστοιχίας", "Move array")}</Button>
        <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={panelSnap}
          onChange={event => setPanelSnap(event.target.checked)} />}
          label={tr("Αυτόματο κούμπωμα", "Auto snap")} />
      </>}
      {groundRows && onGroundRowsChange && !drawing && <>
        <Button size="small" variant={!moveGroundGroup ? "contained" : "outlined"} aria-pressed={!moveGroundGroup}
          onClick={() => { setMoveGroundGroup(false); setError(""); }}>{tr("Μεμονωμένη σειρά", "Individual row")}</Button>
        <Button size="small" variant={moveGroundGroup ? "contained" : "outlined"} aria-pressed={moveGroundGroup}
          disabled={!groundRows.layout.tables.length}
          onClick={() => { setMoveGroundGroup(true); setError(""); }}>{tr("Μετακίνηση όλης της ομάδας", "Move entire group")}</Button>
      </>}
      <Button size="small" startIcon={drawing ? undefined : <AddIcon />} onClick={() => { finishOutline(); setDrawing(!drawing); setError(""); setDrawingKind("polygon"); setPoints([]); setSelected(null); }}>
        {drawing ? tr("Ακύρωση σχεδίασης", "Cancel drawing") : tr("Σχεδίαση εμποδίου", "Draw obstacle")}
      </Button>
      {drawing && <Button size="small" disabled={!validPolygon(points)} onClick={commit}>{tr("Ολοκλήρωση περιγράμματος", "Finish footprint")}</Button>}
      {drawing && <Button size="small" onClick={() => { setPoints(points.slice(0, -1)); setError(""); }}>{tr("Αναίρεση κορυφής", "Undo vertex")}</Button>}
      <Button size="small" onClick={fit}>{tr("Προσαρμογή στη γεωμετρία", "Fit geometry")}</Button>
      <Button size="small" component="label">{tr("Εισαγωγή κάτοψης", "Upload plan")}
        <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} />
      </Button>
      <Button size="small" disabled={!mapOrigin} variant={satellite ? "contained" : "outlined"}
        onClick={() => { setSatellite(!satellite); setTileError(false); }}>{tr("Δορυφορικό υπόβαθρο", "Satellite background")}</Button>
    </Stack>
    {image && !satellite && <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ my: 1 }}>
      {[
        [tr("Πλάτος εικόνας στο έδαφος (m)", "Image ground width (m)"), imageWidth, setImageWidth],
        [tr("Κέντρο ανατολικά (m)", "Centre east (m)"), imageX, setImageX],
        [tr("Κέντρο βόρεια (m)", "Centre north (m)"), imageY, setImageY],
        [tr("Περιστροφή εικόνας (°)", "Image rotation (°)"), rotation, setRotation],
      ].map(([label, val, setter], i) => <TextField key={label} label={label} value={val} size="small" type="number"
        onChange={(e) => setter(i === 0 ? Math.max(0.1, Number(e.target.value)) : Number(e.target.value))} sx={{ width: 210 }} />)}
      <Button onClick={() => setImage(null)}>{tr("Αφαίρεση εικόνας", "Remove image")}</Button>
      <Typography variant="caption">{tr("Ελέγξτε κλίμακα και βορρά· η εικόνα δεν αποθηκεύεται.", "Check scale and north; the image is not saved.")}</Typography>
    </Stack>}
    {outlineFace && !drawing && <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}
      sx={{ px: 1, py: 0.6, mb: 0.6, borderRadius: 1, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" fontWeight={600}>{tr("Επεξεργασία περιγράμματος", "Edit outline")} · {faceNames[outlineFace.id] || outlineFace.label}</Typography>
      <Typography variant="body2">{tr("Σύρετε κορυφές · μήκη και γωνίες κάτοψης", "Drag corners · plan lengths and angles")}</Typography>
      {outlineFace.ring.some((p) => isSharedVertex(outlineFace, p)) && <Typography variant="body2" color="text.secondary">
        {tr("Γκρι κορυφές: κοινές με άλλη πλευρά, κλειδωμένες", "Grey corners: shared with another face, locked")}</Typography>}
      <Button size="small" variant="outlined" sx={{ ml: "auto" }} onClick={finishOutline}>{tr("Τέλος επεξεργασίας", "Finish editing")}</Button>
    </Stack>}
    <Typography variant="caption" display="block" sx={{ mb: 0.4, color: panelMove?.snap ? "success.main" : "text.secondary" }}>{drawing
      ? tr("Κλικ για κορυφές· πατήστε «Ολοκλήρωση» όταν κλείσει το περίγραμμα.",
        "Click to place corners; select Finish when the outline is closed.")
      : panelMove?.snap ? (panelMove.snap.kind === "slot"
        ? tr("✓ Κούμπωσε και ευθυγραμμίστηκε · Alt: ελεύθερη θέση.", "✓ Snapped and aligned · Alt: free placement.")
        : tr("✓ Κούμπωσε στην ακμή · Alt: ελεύθερη θέση.", "✓ Snapped to the edge · Alt: free placement."))
      : onEditPanels && moveArray ? tr("Σύρετε ένα πάνελ για μετακίνηση όλης της συστοιχίας της ενεργής πλευράς · Esc: ακύρωση μετακίνησης.",
        "Drag a module to move the entire active face array · Esc: cancel drag.")
      : onEditPanels && !panelSnap ? tr("Σύρετε πάνελ ελεύθερα εντός της πλευράς · δεξί κλικ: ενέργειες · ροδέλα: ζουμ.",
        "Drag modules freely within the face · right-click: actions · wheel: zoom.")
      : onEditPanels ? tr("Σύρετε πάνελ για κούμπωμα · Alt: ελεύθερα · δεξί κλικ: ενέργειες · ροδέλα: ζουμ.",
        "Drag modules to snap · Alt: free placement · right-click: actions · wheel: zoom.")
        : groundRows && moveGroundGroup ? tr("Σύρετε οποιαδήποτε σειρά για μετακίνηση όλης της ενεργής ομάδας · Esc: ακύρωση.",
          "Drag any row to move the entire active group · Esc: cancel.")
        : groundRows ? tr("Σύρετε σειρά: οδηγοί ακμών και ήπιο κούμπωμα · Alt: χωρίς κούμπωμα · λευκές λαβές: μήκος · Esc: ακύρωση · ροδέλα: ζουμ.",
          "Drag a row: edge guides and gentle snapping · Alt: no snapping · white handles: length · Esc: cancel · wheel: zoom.")
        : tr("Κλικ για επιλογή · δεξί κλικ για ενέργειες · ροδέλα για ζουμ.",
          "Click to select · right-click for actions · wheel to zoom.")}</Typography>
    {groundRows && <Typography variant="body2" sx={{ mb: 0.75, height: 22, overflow: "hidden" }}>
      {groundRows.layout.tables[selectedGroundRow] ? <>{groundRows.layout.tables[selectedGroundRow].label} · {tr("Μήκος", "Length")} {(groundRows.layout.tables[selectedGroundRow].columns * groundRows.moduleWidth).toFixed(2)} m · {groundRows.layout.tables[selectedGroundRow].columns} {tr("στήλες", "columns")} · {groundRows.layout.tables[selectedGroundRow].columns * groundRows.tiers} {tr("θέσεις πλαισίων", "module positions")}</> : tr("Επιλέξτε σειρά για μήκος και αποστάσεις.", "Select a row to see length and distances.")}
    </Typography>}
    <Box sx={{ position: "relative", isolation: "isolate", overflow: "hidden", border: "1px solid #cbd5e1", borderRadius: "4px", background: "#f1f5f9" }}>
    {/* A warning must not shift the SVG while a pointer gesture is in progress:
        doing so changes the next pointer-to-world conversion and causes jitter. */}
    {(error || invalidTurn) && <Alert severity="warning"
      sx={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 3, pointerEvents: "none" }}>
      {error || turnError}
    </Alert>}
    {showMap && <PlanMapBackground origin={mapOrigin} bounds={bounds} width={width} height={height} onError={() => setTileError(true)} />}
    <svg ref={svgRef} role="img" aria-label={tr("Κάτοψη γεωμετρίας έργου", "Project geometry plan")}
      viewBox={`${bounds.x} ${bounds.y} ${extent} ${viewHeight}`} style={{ position: "relative", zIndex: 1, display: "block", width: "100%", height: width < 600 ? "clamp(360px, 60vh, 520px)" : "clamp(520px, 68vh, 800px)", touchAction: "none", cursor: drawing ? "crosshair" : "grab" }}
      onPointerDown={(event) => {
        suppressClick.current = false;
        setSelectedGroundRow(null);
        if (event.button !== 0 && event.button !== 1) return;
        {
          event.preventDefault();
          const id = event.target.closest?.('[data-obstacle-id]')?.getAttribute('data-obstacle-id');
          const obstacle = !drawing && event.button === 0 ? obstacles.find((o) => o.id === id) : null;
          const contextId = event.target.getAttribute?.("data-context-id");
          const handleId = event.target.closest?.("[data-azimuth-handle]")?.getAttribute("data-azimuth-handle");
          const azimuthFace = !drawing && event.button === 0 ? roofFaces.find((f) => f.id === handleId && f.editable) : null;
          const vertexEl = !drawing && event.button === 0 ? event.target.closest?.("[data-roof-vertex]") : null;
          const vertexFace = vertexEl && outlineFace?.id === vertexEl.getAttribute("data-roof-vertex") ? outlineFace : null;
          const vertexIndex = vertexEl ? Number(vertexEl.getAttribute("data-vertex-index")) : -1;
          const vertex = vertexFace && Number.isInteger(vertexIndex) && vertexIndex >= 0 && vertexIndex < vertexFace.ring.length
            && !isSharedVertex(vertexFace, vertexFace.ring[vertexIndex])
            ? { face: vertexFace, index: vertexIndex } : null;
          if (vertex) setError("");
          // Pointer capture sends the release (and the click) to the plan itself,
          // so the edge under the press is remembered here and read at release.
          const edge = !drawing && event.button === 0 ? event.target.getAttribute?.("data-azimuth-edge") : null;
          const select = !drawing && event.button === 0 ? event.target.closest?.("[data-select-plane]")?.getAttribute("data-select-plane") : null;
          // The pencil sits inside the badge; a press on it is not a press on the badge.
          const badge = !drawing && event.button === 0 && !select ? event.target.closest?.("[data-face-pv]")?.getAttribute("data-face-pv") : null;
          const pvPanel = !drawing && event.button === 0 && event.target.closest?.("[data-pv-panel]")?.getAttribute("data-pv-panel");
          const movingPanel = pvPanel && onEditPanels && !aligning && !rotatingPanels && !rotatingObjectId && !outlineEditId
            ? { slot: pvPanel, all: moveArray, start: fromEvent(event), delta: { x: 0, y: 0 } } : null;
          if (!drawing && event.button === 0) setSelectedPanel(pvPanel || null);
          if (movingPanel) { setError(""); setPanelMove({ slot: movingPanel.slot, all: movingPanel.all, delta: movingPanel.delta }); }
          const panelRotation = !drawing && rotatingPanels && event.button === 0
            && Boolean(event.target.closest?.("[data-panel-rotation-handle]"));
          const objectRotation = objectRotationVisible && event.button === 0 && event.target.closest?.("[data-object-rotation-handle]")
            ? { obstacle: rotatingObject, owner: rotatingShared?.planeId } : null;
          if (obstacle && !azimuthFace) setSelected(obstacle.id);
          // Another face's object: dragged here like this face's own.
          const sharedEl = !drawing && event.button === 0 && onSharedObstacleMove ? event.target.closest?.("[data-shared-obstacle-id]") : null;
          const shared = sharedEl ? (sharedObstacles || []).find((x) => x.planeId === sharedEl.getAttribute("data-shared-plane")
            && x.obstacle.id === sharedEl.getAttribute("data-shared-obstacle-id")) || null : null;
          if (shared && !azimuthFace) setSelected(`shared:${shared.planeId}:${shared.obstacle.id}`);
          const pressedFace = azimuthFace?.id || vertex?.face.id
            || (edge ? edge.slice(0, edge.lastIndexOf(":")) : null)
            || (contextId ? `plane:${contextId}` : null)
            || (pvPanel || event.target.hasAttribute?.("data-active-roof") ? "roof" : null);
          if (!drawing && event.button === 0 && pressedFace) setSelected(pressedFace);
          drag.current = { x: event.clientX, y: event.clientY, centre, obstacle: azimuthFace || vertex || badge || select || obstacle?.kind === "roof" ? null : obstacle,
            shared: azimuthFace || vertex || badge || select || shared?.obstacle.kind === "roof" ? null : shared,
            lockedFace: !drawing && event.button === 0 && (objectRotation || panelRotation || vertex || event.target.hasAttribute?.("data-locked-roof-vertex") || pvPanel || event.target.hasAttribute?.("data-active-roof") || Boolean(contextId) || obstacle?.kind === "roof" || shared?.obstacle.kind === "roof"),
            azimuthFace, vertex, edge, badge, select, panelRotation, objectRotation, panel: movingPanel,
            // A press and release on another face (no drag) makes it the edited one.
            contextSelect: !drawing && event.button === 0 && !badge && !select && contextId ? contextId : null,
            before: value };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }
      }}
      onPointerMove={(event) => {
        if (drag.current) {
          const d = drag.current, dx = event.clientX - d.x, dy = event.clientY - d.y;
          if (!suppressClick.current && Math.hypot(dx, dy) < 3) return;
          suppressClick.current = true;
          if (d.panel) {
            const p = fromEvent(event);
            const delta = { x: p.x - d.panel.start.x, y: p.y - d.panel.start.y };
            const movingQuads = panelQuads.filter(q => d.panel.all || q.slot === d.panel.slot);
            const snapped = event.altKey || !panelSnap || d.panel.all ? { delta, snapped: false }
              : snapModuleDelta(panelQuads, [d.panel.slot], delta, {
                threshold: Math.max(0.1, Math.min(0.4, 20 * extent / width)), gap: 0,
                valid: quads => modulesFitRoof(quads, value.roof),
              });
            if (!modulesFitRoof(movingQuads, value.roof, snapped.delta)) {
              setError(tr("Η μετακίνηση πρέπει να παραμένει μέσα στην πλευρά, με την απόσταση από τις ακμές που έχει οριστεί.",
                "Keep the modules inside the roof face, respecting its edge setback."));
              return;
            }
            setError("");
            d.panel.delta = snapped.delta;
            setPanelMove({ slot: d.panel.slot, all: d.panel.all, delta: snapped.delta,
              snap: snapped.snapped ? { target: snapped.target, kind: snapped.kind } : null });
          }
          else if (d.objectRotation) {
            const { obstacle: o, owner } = d.objectRotation, p = fromEvent(event);
            const bearing = bearingOf(p.x - Number(o.eastM), p.y - Number(o.northM));
            if (bearing != null) {
              const patch = obstacleRotationPatch(o, Math.round(bearing * 10) / 10);
              if (owner != null) onSharedObstacleChange?.(owner, o.id, patch);
              else onChange({ ...d.before, obstacles: d.before.obstacles.map(item => item.id === o.id ? { ...item, ...patch } : item) });
            }
          }
          else if (d.azimuthFace) {
            // Drag freely; an edge click is the separate eave control.
            const p = fromEvent(event);
            const bearing = bearingOf(p.x - d.azimuthFace.centre.x, p.y - d.azimuthFace.centre.y);
            const deg = roundAzimuth(bearing);
            if (deg != null) setAzimuthPreview({ id: d.azimuthFace.id, deg });
          }
          else if (d.panelRotation && panelCentre) {
            const p = fromEvent(event);
            const bearing = bearingOf(p.x - panelCentre.x, p.y - panelCentre.y);
            if (bearing != null) {
              const next = Math.round((bearing % 360) * 10) / 10;
              if (!panelTurnAllowed(rotatedModuleQuadsToAxis(panelQuads, next), value)) {
                setError(turnError);
                return;
              }
              setError("");
              d.panelBearing = next;
              setPanelAxisDraft(String(d.panelBearing));
            }
          }
          else if (d.vertex) previewVertex(d.vertex.face, d.vertex.index, fromEvent(event));
          else if (d.obstacle) onChange({ ...d.before, obstacles: d.before.obstacles.map((o) => o.id === d.obstacle.id
            ? { ...o, eastM: roundMetres(Number(o.eastM) + dx * extent / width), northM: roundMetres(Number(o.northM) - dy * extent / width) } : o) });
          else if (d.shared) onSharedObstacleMove(d.shared.planeId, d.shared.obstacle.id,
            roundMetres(Number(d.shared.obstacle.eastM) + dx * extent / width),
            roundMetres(Number(d.shared.obstacle.northM) - dy * extent / width));
          else if (!d.lockedFace) setCentre({ x: d.centre.x - dx * extent / width, y: d.centre.y - dy * extent / width });
        }
        else if (drawing) { const point = fromEvent(event); setHover(snap(point) || point); }
      }}
      onPointerUp={() => {
        const d = drag.current;
        drag.current = null;
        setPanelMove(null);
        if (!d) return;
        if (d.panel && suppressClick.current) {
          if (Math.hypot(d.panel.delta.x, d.panel.delta.y) > 1e-10) {
            onEditPanels?.(d.panel.all ? { type: "move-array", delta: d.panel.delta }
              : { type: "move", slot: d.panel.slot, delta: d.panel.delta });
          }
        } else if (d.azimuthFace) {
          if (azimuthPreview?.id === d.azimuthFace.id) setFaceAzimuth(d.azimuthFace.id, azimuthPreview.deg);
          setAzimuthPreview(null);
        } else if (d.panelRotation && d.panelBearing != null) {
          commitPanelTurn(d.panelBearing);
        } else if (d.vertex && suppressClick.current) {
          commitVertexPreview();
        } else if (d.badge && !suppressClick.current) {
          badgeAction(d.badge);
        } else if (d.select && !suppressClick.current) {
          onSelectPlane?.(d.select);
        } else if (d.contextSelect && !suppressClick.current) {
          onSelectPlane?.(d.contextSelect);
        } else if (d.edge && !suppressClick.current) {
          // A press and release on an edge: that edge is the eave.
          const split = d.edge.lastIndexOf(":"), faceId = d.edge.slice(0, split), index = d.edge.slice(split + 1);
          const face = roofFaces.find((f) => f.id === faceId && f.editable);
          if (face) setFaceEave(face.id, Number(index));
        }
      }}
      onPointerCancel={() => {
        const d = drag.current;
        if (d && suppressClick.current) {
          if (d.objectRotation) {
            const { obstacle: o, owner } = d.objectRotation;
            if (owner != null) onSharedObstacleChange?.(owner, o.id, { rotationDeg: o.rotationDeg,
              ...(o.kind === "polygon" ? { footprint: o.footprint, widthM: o.widthM, depthM: o.depthM } : {}) });
            else onChange(d.before);
          }
          else if (d.azimuthFace) setAzimuthPreview(null);
          else if (d.panelRotation) setPanelAxisDraft(String(panelAxis ?? 0));
          else if (d.vertex) clearVertexPreview();
          else if (d.obstacle) onChange(d.before);
          else if (d.shared) onSharedObstacleMove(d.shared.planeId, d.shared.obstacle.id, d.shared.obstacle.eastM, d.shared.obstacle.northM);
          setCentre(d.centre);
        }
        drag.current = null;
        setPanelMove(null);
      }}
      onLostPointerCapture={() => { drag.current = null; setPanelMove(null); clearVertexPreview(); }}
      onPointerLeave={() => setHover(null)}
      onContextMenu={(event) => {
        if (drawing) return;
        const missing = event.target.closest?.("[data-missing-panel]")?.getAttribute("data-missing-panel");
        if (onEditPanels && missing) {
          event.preventDefault(); setMenu({ x: event.clientX, y: event.clientY, missing }); return;
        }
        const panel = event.target.closest?.("[data-pv-panel]")?.getAttribute("data-pv-panel");
        if ((onAlignPanels || onRotatePanels || onEditPanels) && panel) {
          event.preventDefault(); setSelected("roof"); setSelectedPanel(panel); setMenu({ x: event.clientX, y: event.clientY, pv: true, pvSlot: panel }); return;
        }
        const editFaceId = event.target.closest?.("[data-outline-face], [data-azimuth-handle]")?.getAttribute("data-outline-face")
          || event.target.closest?.("[data-azimuth-handle]")?.getAttribute("data-azimuth-handle");
        const obstacleId = event.target.closest?.("[data-obstacle-id]")?.getAttribute("data-obstacle-id")
          || (editFaceId && !editFaceId.startsWith("shared:") && editFaceId !== "roof" ? editFaceId : null);
        const planeId = event.target.getAttribute?.("data-context-id");
        const roof = event.target.hasAttribute?.("data-active-roof") || editFaceId === "roof";
        const sharedEl = event.target.closest?.("[data-shared-obstacle-id]");
        const shared = sharedEl ? (sharedObstacles || []).find((x) => x.planeId === sharedEl.getAttribute("data-shared-plane")
          && x.obstacle.id === sharedEl.getAttribute("data-shared-obstacle-id")) || null
          : (sharedObstacles || []).find((x) => `shared:${x.planeId}:${x.obstacle.id}` === editFaceId) || null;
        if (!obstacleId && !planeId && !roof && !shared) return;
        event.preventDefault();
        setSelected(roof ? "roof" : obstacleId || (shared ? `shared:${shared.planeId}:${shared.obstacle.id}` : `plane:${planeId}`));
        setMenu({ x: event.clientX, y: event.clientY, obstacle: obstacles.find((o) => o.id === obstacleId) || null,
          plane: planeId ? contextOutlines.find((o) => o.planeId === planeId) || null : null, roof, shared,
          point: roof && onEditPanels ? fromEvent(event) : null });
      }}
      onClick={(event) => {
        if (suppressClick.current) return;
        const raw = fromEvent(event), point = snap(raw) || raw;
        if (drawing) {
          if (points.length >= 3 && Math.hypot(point.x - points[0].x, point.y - points[0].y) < 1e-5) { commit(); return; }
          if (!canAppendVertex(points, point) || points.length >= 200) {
            setError(tr("Η ακμή τέμνει ή επικαλύπτει άλλη ακμή, ή επαναλαμβάνει κορυφή. Επιλέξτε άλλο σημείο (έως 200 κορυφές).", "This edge crosses or overlaps another edge, or repeats a vertex. Choose another point (up to 200 vertices).")); return;
          }
          setPoints([...points, point]); setError("");
        }
      }}>
      {image && !satellite && <image href={image.url} x={imageX - imageWidth / 2} y={-imageY - imageWidth * image.ratio / 2}
        width={imageWidth} height={imageWidth * image.ratio} opacity={0.65} transform={`rotate(${rotation} ${imageX} ${-imageY})`} />}
      {groundBoundary.length > 2 && <polygon data-testid="ground-parcel-outline" points={path(groundBoundary)} fill="none" stroke="#f59e0b" strokeWidth={extent / width * 2.5} pointerEvents="none" />}
      {contextGroundGroups.map(group => <g key={group.id} data-ground-group={group.id} role="group" aria-label={`${tr("Επίγεια ομάδα", "Ground group")} ${group.label}`}>
        {group.rows.map((points, i) => <polygon key={i} data-context-id={group.id}
          points={path(points)} fill="#8b5cf6" fillOpacity={0.55} stroke="#6d28d9" strokeWidth={extent / width}
          style={{ cursor: "pointer" }}>
          <title>{tr("Ομάδα", "Group")} {group.label} · {tr("Κλικ για επεξεργασία", "Click to edit")}</title>
        </polygon>)}
        {group.rows[0]?.length > 0 && <text pointerEvents="none" x={group.rows[0][0].x} y={-group.rows[0][0].y - extent / width * 8}
          fontSize={extent / width * 12} fill="#6d28d9" stroke="#fff" strokeWidth={extent / width * 3} paintOrder="stroke">
          {tr("Ομάδα", "Group")} {group.label}
        </text>}
      </g>)}
      {groundRows && !drawing && <GroundRowsLayer {...groundRows} onChange={onGroundRowsChange}
        moveGroup={moveGroundGroup}
        fromEvent={fromEvent} scale={extent / width} greek={greek} onError={setError}
        selected={selectedGroundRow} onSelect={index => { setSelectedGroundRow(index); setSelected(null); }} />}
      <line pointerEvents="none" x1={bounds.x} x2={bounds.x + extent} y1="0" y2="0" stroke="#94a3b8" strokeWidth={extent / width} />
      <line pointerEvents="none" y1={bounds.y} y2={bounds.y + viewHeight} x1="0" x2="0" stroke="#94a3b8" strokeWidth={extent / width} />
      {contextOutlines.map((o) => <polygon key={o.id} points={path(o.points)} fill="#dbeafe" fillOpacity={0.18}
        stroke={o.roof && selected === `plane:${o.planeId}` ? "#ef4444" : "#64748b"} strokeWidth={extent / 500} data-context-id={o.planeId}
        onClick={(event) => { if (!drawing && !suppressClick.current && o.roof) { event.stopPropagation(); setSelected(`plane:${o.planeId}`); } }}
        style={{ cursor: drawing ? "crosshair" : onSelectPlane ? "pointer" : "default" }}>
        <title>{tr("Πλευρά", "Surface")} {o.label}{onSelectPlane ? tr(" · κλικ για επεξεργασία", " · click to edit") : ""}</title>
      </polygon>)}
      {value.roof && <polygon data-active-roof="true" style={{ cursor: drawing ? "crosshair" : "default" }} points={path(roofPoints)} fill="#dbeafe" fillOpacity={0.3}
        stroke={selected === "roof" ? "#ef4444" : "#2563eb"} strokeWidth={extent / 500}
        onClick={(event) => { if (!drawing && !suppressClick.current) { event.stopPropagation(); setSelected("roof"); } }}>
        <title>{faceNames.roof || tr("Επιλεγμένη πλευρά με Φ/Β", "Selected PV roof face")}</title>
      </polygon>}
      {obstacles.map((o) => <g key={o.id} data-obstacle-id={o.id} style={{ cursor: drawing ? "crosshair" : o.kind === "roof" ? "default" : "grab" }} onClick={(e) => { if (!drawing && !suppressClick.current) { e.stopPropagation(); setSelected(o.id); } }}>
        {o.kind === "tree" ? <ellipse cx={o.eastM} cy={-o.northM} rx={o.widthM / 2} ry={o.depthM / 2} fill="#86b88a" stroke={selected === o.id ? "#ef4444" : "#166534"} strokeWidth={extent / 300} />
          : <polygon points={path(vertexPreview?.id === o.id ? vertexPreview.ring : obstacleFootprint(o))} fill={o.kind === "roof" ? "#d9b9a2" : "#cbd5e1"} fillOpacity={o.kind === "roof" ? 0.3 : 1} stroke={selected === o.id ? "#ef4444" : "#475569"} strokeWidth={extent / 300} />}
        <title>{o.kind === "roof" ? faceNames[o.id] || o.name || tr("Πλευρά στέγης", "Roof face") : o.name || ""}</title>
        {/* Faces carry their Φ/Β badge instead of a number. */}
        {o.kind !== "roof" && <text x={o.eastM} y={-o.northM} fontSize={extent / 45} textAnchor="middle" fill="#0f172a">{obstacles.filter((x) => x.kind !== "roof").indexOf(o) + 1}</text>}
      </g>)}
      {(sharedObstacles || []).map(({ planeId, obstacle: o }, i) => <g key={`shared-${planeId}-${o.id}`}
        data-shared-obstacle-id={o.id} data-shared-plane={planeId}
        style={{ cursor: drawing ? "crosshair" : o.kind === "roof" ? "default" : onSharedObstacleMove ? "grab" : undefined }}
        onClick={(e) => { if (!drawing && !suppressClick.current) { e.stopPropagation(); setSelected(`shared:${planeId}:${o.id}`); } }}>
        {o.kind === "tree" ? <ellipse cx={o.eastM} cy={-o.northM} rx={o.widthM / 2} ry={o.depthM / 2} fill="#86b88a" fillOpacity={0.85} stroke={selected === `shared:${planeId}:${o.id}` ? "#ef4444" : "#166534"} strokeDasharray={`${extent / 150} ${extent / 250}`} strokeWidth={extent / 300} />
          : <polygon points={path(vertexPreview?.id === `shared:${planeId}:${o.id}` ? vertexPreview.ring : obstacleFootprint(o))} fill={o.kind === "roof" ? "#d9b9a2" : "#cbd5e1"} fillOpacity={o.kind === "roof" ? 0.3 : 0.85} stroke={selected === `shared:${planeId}:${o.id}` ? "#ef4444" : "#475569"} strokeDasharray={`${extent / 150} ${extent / 250}`} strokeWidth={extent / 300} />}
        <title>{o.kind === "roof" ? faceNames[`shared:${planeId}:${o.id}`] || o.name || tr("Πλευρά στέγης χωρίς Φ/Β", "Roof face without PV") : o.name || tr("Εμπόδιο σκίασης", "Shading obstacle")}</title>
        {o.kind !== "roof" && <text x={o.eastM} y={-o.northM} fontSize={extent / 45} textAnchor="middle" fill="#0f172a">
          {obstacles.filter((x) => x.kind !== "roof").length + (sharedObstacles || []).filter((x) => x.obstacle.kind !== "roof").findIndex((x) => x.planeId === planeId && x.obstacle.id === o.id) + 1}
        </text>}
      </g>)}
      {missingPanels.map(q => <polygon key={`missing-${q.slot}`} data-missing-panel={q.slot}
        points={path([q.low[0], q.low[1], q.high[1], q.high[0]])} fill="transparent" stroke="#b45309"
        strokeWidth={extent / width} strokeDasharray={`${extent / width * 4} ${extent / width * 3}`}>
        <title>{tr("Αφαιρεμένο πάνελ · δεξί κλικ για επαναφορά", "Removed module · right-click to restore")}</title>
      </polygon>)}
      {previewPanels.map((q) => <polygon key={`pv-${q.slot}`} data-pv-panel={onAlignPanels || onRotatePanels || onEditPanels ? q.slot : undefined}
        data-panel-snap={panelMove?.snap && (q.slot === panelMove.slot || q.slot === panelMove.snap.target) ? "true" : undefined}
        pointerEvents={onAlignPanels || onRotatePanels || onEditPanels ? "visiblePainted" : "none"}
        points={path([q.low[0], q.low[1], q.high[1], q.high[0]])}
        fill={unassignedSlots.includes(q.slot) ? "#f59e0b" : "#518bb7"} fillOpacity={0.85}
        style={{ cursor: onEditPanels ? (panelMove?.all || panelMove?.slot === q.slot ? "grabbing" : "grab") : "default" }}
        stroke={panelMove?.snap && (q.slot === panelMove.slot || q.slot === panelMove.snap.target) ? "#16a34a"
          : moveArray || selectedPanel === q.slot ? "#d97706" : "#e2e8f0"} strokeWidth={extent / width * (moveArray || selectedPanel === q.slot || panelMove?.snap?.target === q.slot ? 2 : 1)}>
        <title>{unassignedSlots.includes(q.slot) ? tr("Χωρίς string · σύρετε για μετακίνηση · δεξί κλικ για ενέργειες", "Without a string · drag to move · right-click for actions")
          : onEditPanels ? tr("Σύρετε για μετακίνηση · δεξί κλικ για ενέργειες πάνελ", "Drag to move · right-click for module actions")
          : tr("Δεξί κλικ για ενέργειες πάνελ", "Right-click for module actions")}</title>
      </polygon>)}
      {panelClearances.map(g => <g key={g.id} data-panel-clearance={g.kind} pointerEvents="none">
        <line x1={g.a.x} y1={-g.a.y} x2={g.b.x} y2={-g.b.y} stroke="#075985"
          strokeWidth={extent / width * 1.5} strokeDasharray={`${extent / width * 5} ${extent / width * 4}`} />
        <text x={(g.a.x + g.b.x) / 2} y={-(g.a.y + g.b.y) / 2 - extent / width * 8}
          textAnchor="middle" fontSize={extent / width * 12} fill="#075985" stroke="#fff" strokeWidth={extent / width * 4} paintOrder="stroke">
          {g.distance > 1e-8 && g.distance < .01 ? `<${(.01).toLocaleString(greek ? "el-GR" : "en-GB", { minimumFractionDigits: 2 })}`
            : g.distance.toLocaleString(greek ? "el-GR" : "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m
        </text>
      </g>)}
      {rotatingPanels && panelCentre && <g aria-label={tr("Χειριστήριο περιστροφής συστοιχίας", "Array rotation handle")}
        data-panel-rotation-handle="true" style={{ cursor: "grab" }}>
        <line x1={panelCentre.x} y1={-panelCentre.y} x2={handleX} y2={handleY}
          stroke="#d97706" strokeWidth={extent / width * 2} strokeDasharray={`${extent / width * 5} ${extent / width * 4}`} pointerEvents="none" />
        <circle cx={handleX} cy={handleY}
          r={extent / width * 12} fill="#fff7ed" stroke="#d97706" strokeWidth={extent / width * 2.5} />
        <text x={handleX} y={handleY}
          textAnchor="middle" dominantBaseline="central" fontSize={extent / width * 13} fill="#9a3412" pointerEvents="none">↻</text>
        <text x={handleX} y={handleY - extent / width * 20}
          textAnchor="middle" fontSize={extent / width * 13} fontWeight="700" fill="#9a3412"
          stroke="#fff7ed" strokeWidth={extent / width * 3} paintOrder="stroke" pointerEvents="none">
          {shownBearing}°
        </text>
      </g>}
      {/* The eaves: where each face's slope ends, and what sets its facing. */}
      {!drawing && [...allRoofFaces.map((f) => ({ key: `eave-${f.id}`, ring: f.ring,
        edge: f.eave, colour: f.pv ? "#1d4ed8" : "#9a3412" })),
        ...contextOutlines.filter((o) => Number.isInteger(o.eaveEdge) && o.eaveEdge >= 0 && o.eaveEdge < o.points.length)
          .map((o) => ({ key: `eave-${o.id}`, ring: o.points, edge: o.eaveEdge, colour: "#64748b" }))]
        .filter((e) => Number.isInteger(e.edge)).map((e) => {
          const p = e.ring[e.edge], q = e.ring[(e.edge + 1) % e.ring.length];
          return <line key={e.key} data-eave={e.key} x1={p.x} y1={-p.y} x2={q.x} y2={-q.y} stroke={e.colour}
            strokeWidth={extent / width * 3.5} strokeLinecap="round" opacity={0.85} pointerEvents="none" />;
        })}
      {!drawing && roofFaces.map((face) => {
        const px = extent / width;                       // one screen pixel, in metres
        const azimuth = azimuthPreview?.id === face.id ? azimuthPreview.deg : face.azimuth;
        const d = compass(azimuth);
        const at = (along, side = 0) => `${face.centre.x + d.x * along - d.y * side},${-(face.centre.y + d.y * along + d.x * side)}`;
        const tip = face.length;                         // the head's point
        const colour = face.id === "roof" ? "#1d4ed8" : "#9a3412";
        return <g key={`az-${face.id}`} aria-label={`${face.label}: ${tr("αζιμούθιο", "azimuth")} ${azimuth}°`}>
          {face.editable && face.ring.map((p, i) => {
            const q = face.ring[(i + 1) % face.ring.length];
            const hovered = hoverEdge === `${face.id}:${i}`;
            return <line key={i} x1={p.x} y1={-p.y} x2={q.x} y2={-q.y} stroke={hovered ? "#f59e0b" : "transparent"}
              strokeWidth={px * (hovered ? 4 : 10)} strokeLinecap="round" pointerEvents="stroke" style={{ cursor: "pointer" }}
              data-azimuth-edge={`${face.id}:${i}`}
              onPointerEnter={() => setHoverEdge(`${face.id}:${i}`)} onPointerLeave={() => setHoverEdge(null)}>
              <title>{tr("Κλικ: γείσο σε αυτή την ακμή· η πλευρά κατηφορίζει προς αυτή", "Click: make this edge the eave; the face slopes down to it")} ({roundAzimuth(edgeOutwardAzimuth(face.ring, i))}°)</title>
            </line>;
          })}
          {/* A thin shaft from the centre, a closed head, and the degrees in a
              pill just past the head. All of it drags, not only a small dot. */}
          <g data-azimuth-handle={face.editable ? face.id : undefined} style={{ cursor: face.editable ? "grab" : undefined }}>
            <circle cx={face.centre.x} cy={-face.centre.y} r={px * 2.5} fill={colour} />
            <line x1={face.centre.x} y1={-face.centre.y} x2={face.centre.x + d.x * (tip - px * 9)} y2={-(face.centre.y + d.y * (tip - px * 9))}
              stroke="white" strokeWidth={px * 3} strokeLinecap="round" opacity={0.8} />
            <line x1={face.centre.x} y1={-face.centre.y} x2={face.centre.x + d.x * (tip - px * 9)} y2={-(face.centre.y + d.y * (tip - px * 9))}
              stroke={colour} strokeWidth={px * 1.4} strokeLinecap="round" />
            <polygon points={`${at(tip)} ${at(tip - px * 10, px * 4)} ${at(tip - px * 10, -px * 4)}`}
              fill={colour} stroke="white" strokeWidth={px} strokeLinejoin="round" />
            <g transform={`translate(${face.centre.x + d.x * (tip + px * 13)},${-(face.centre.y + d.y * (tip + px * 13))}) scale(${px})`}>
              <rect x="-17" y="-8" width="34" height="16" rx="8" fill="white" fillOpacity="0.95" stroke={colour} strokeWidth="1" />
              <text fontSize="10" fontWeight="700" textAnchor="middle" dominantBaseline="central" fill={colour}>{azimuth}°</text>
            </g>
            {face.editable && <title>{tr("Σύρετε για ελεύθερο αζιμούθιο· κλικ σε ακμή για επιλογή γείσου", "Drag for a free azimuth; click an edge to choose the eave")}</title>}
          </g>
        </g>;
      })}
      {!drawing && outlineFace && (() => {
        const face = outlineFace, px = extent / width, measurements = outlineMeasurements(face.ring);
        const colour = vertexPreview?.id === face.id && !validPolygon(face.ring) ? "#dc2626" : "#1d4ed8";
        const format = (n, digits) => n.toLocaleString(greek ? "el-GR" : "en-GB", { maximumFractionDigits: digits });
        const label = (point, text, kind, index) => <g key={`${kind}-${index}`} data-outline-measure={kind} data-measure-index={index}
          transform={`translate(${point.x},${-point.y}) scale(${px})`} pointerEvents="none">
          <rect x={-(text.length * 7.4 + 12) / 2} y="-10" width={text.length * 7.4 + 12} height="20" rx="4"
            fill="white" fillOpacity="0.96" stroke={kind === "length" ? colour : "#cbd5e1"} />
          <text textAnchor="middle" dominantBaseline="central" fontSize="13" fontWeight={kind === "length" ? 600 : 500}
            fill={kind === "length" ? colour : "#334155"}>{text}</text>
        </g>;
        return <g data-outline-face={face.id} aria-label={tr("Μήκη και γωνίες κάτοψης", "Plan lengths and angles")}>
          <polygon points={path(face.ring)} fill="none" stroke={colour} strokeWidth={px * 2.5} pointerEvents="none" />
          {measurements.map((m, i) => label({ x: m.midpoint.x + m.outward.x * px * 18,
            y: m.midpoint.y + m.outward.y * px * 18 }, `${format(m.length, 2)} m`, "length", i))}
          {measurements.map((m, i) => label({ x: face.ring[i].x + m.inward.x * px * 32,
            y: face.ring[i].y + m.inward.y * px * 32 }, m.angle == null ? "—" : `${format(m.angle, 1)}°`, "angle", i))}
          {face.ring.map((point, index) => {
            const locked = isSharedVertex(face, point);
            return <circle key={`vertex-${index}`} data-roof-vertex={locked ? undefined : face.id}
              data-locked-roof-vertex={locked ? face.id : undefined} data-vertex-index={index}
              cx={point.x} cy={-point.y} r={px * 7} fill={locked ? "#e2e8f0" : "white"} stroke={locked ? "#64748b" : colour}
              strokeWidth={px * 2} style={{ cursor: locked ? "not-allowed" : "move" }}>
              <title>{locked ? tr("Κοινή κορυφή με γειτονική πλευρά · κλειδωμένη", "Shared corner with a neighbouring face · locked")
                : tr("Σύρετε για αλλαγή περιγράμματος", "Drag to reshape the outline")}</title>
            </circle>;
          })}
        </g>;
      })()}
      {pvBadges.map((badge) => {
        const px = extent / width;
        const clickable = badge.obstacle || (badge.planeId && !badge.primary);
        const title = badge.obstacle ? tr("Κλικ: αυτή η πλευρά παίρνει Φ/Β και γίνεται προσανατολισμός", "Click: this face gets PV and becomes an orientation")
          : badge.active ? tr("Η πλευρά που επεξεργάζεστε", "The face being edited")
          : badge.primary ? tr("Ο πρώτος προσανατολισμός κρατά τα Φ/Β του", "The first orientation keeps its PV")
          : tr("Κλικ: αφαίρεση Φ/Β· η πλευρά μένει ως σκίαση και τα MPPT της γυρίζουν στον πρώτο προσανατολισμό", "Click: remove PV; the face stays as shading and its MPPTs return to the first orientation");
        const fill = badge.pv ? (badge.active ? "#1d4ed8" : "#3b82f6") : "white";
        const text = badge.pv ? "Φ/Β ✓" : "+ Φ/Β";
        return <g key={badge.key} transform={`translate(${badge.centre.x},${-badge.centre.y - px * 16}) scale(${px})`}
          style={{ cursor: clickable ? "pointer" : "default" }} data-face-pv={clickable ? badge.key : undefined}
          aria-label={`${badge.label ? `${tr("Πλευρά", "Surface")} ${badge.label}: ` : ""}${badge.pv ? tr("με Φ/Β", "with PV") : tr("χωρίς Φ/Β", "without PV")}`}>
          <rect x="-24" y="-10" width="48" height="20" rx="10" fill={fill} fillOpacity="0.95" stroke={badge.pv ? "white" : "#1d4ed8"} strokeWidth="1.2" />
          <text fontSize="11" fontWeight="700" textAnchor="middle" dominantBaseline="central" fill={badge.pv ? "white" : "#1d4ed8"}>{text}</text>
          <title>{title}</title>
          {badge.planeId && onSelectPlane && <g transform="translate(34,0)" data-select-plane={badge.planeId} style={{ cursor: "pointer" }}
            aria-label={`${tr("Επεξεργασία πλευράς", "Edit surface")} ${badge.label}`}>
            <circle r="10" fill="white" fillOpacity="0.95" stroke="#1d4ed8" strokeWidth="1.2" />
            <text fontSize="11" textAnchor="middle" dominantBaseline="central" fill="#1d4ed8">✎</text>
            <title>{tr("Κλικ: επεξεργασία αυτής της πλευράς", "Click: edit this face")}</title>
          </g>}
        </g>;
      })}
      {selectedRoofFace && selectedRoofName && !drawing && <text data-selected-face-name="true"
        x={selectedRoofFace.centre.x} y={-selectedRoofFace.centre.y + extent / width * 18}
        textAnchor="middle" fontSize={extent / width * 12} fontWeight="600"
        fill="#7f1d1d" stroke="white" strokeWidth={extent / width * 3}
        paintOrder="stroke" pointerEvents="none">{selectedRoofName}</text>}
      {points.length > 0 && <polyline points={path(points)} fill="none" stroke="#e56b17" strokeWidth={extent / 300} />}
      {drawing && hover && points.length > 0 && <line x1={points[points.length - 1].x} y1={-points[points.length - 1].y}
        x2={hover.x} y2={-hover.y} stroke="#e56b17" strokeDasharray={`${extent / width * 5}`} strokeWidth={extent / width * 2} />}
      {points.map((p, i) => <circle key={i} cx={p.x} cy={-p.y} r={extent / width * (i === 0 ? 7 : 4)} fill={i === 0 ? "#16a34a" : "#e56b17"} stroke="white" strokeWidth={extent / width} />)}
      {drawing && hover && <circle cx={hover.x} cy={-hover.y} r={extent / width * 8} fill="none" stroke="#0ea5e9" strokeWidth={extent / width * 2} />}
      {drawing && points.length > 0 && (() => {
        // Edge lengths while drawing: each placed edge, and the one following
        // the cursor, live. Offset to the left of the drawing direction so the
        // label never sits on the line it measures.
        const px = extent / width;
        const format = (n) => `${n.toLocaleString(greek ? "el-GR" : "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`;
        const label = (a, b, live, key) => {
          const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy);
          if (length < px * 6) return null;
          const at = { x: (a.x + b.x) / 2 - dy / length * px * 16, y: (a.y + b.y) / 2 + dx / length * px * 16 };
          const text = format(length), w = text.length * 7.4 + 12;
          return <g key={key} data-drawing-edge-length={live ? "live" : "placed"} pointerEvents="none"
            transform={`translate(${at.x},${-at.y}) scale(${px})`}>
            <rect x={-w / 2} y="-10" width={w} height="20" rx="4" fill="white" fillOpacity="0.96"
              stroke={live ? "#e56b17" : "#cbd5e1"} />
            <text textAnchor="middle" dominantBaseline="central" fontSize="13" fontWeight={live ? 700 : 500}
              fill={live ? "#c2410c" : "#334155"}>{text}</text>
          </g>;
        };
        return <g aria-label={tr("Μήκη ακμών σχεδίασης", "Drawing edge lengths")}>
          {points.slice(1).map((p, i) => label(points[i], p, false, `placed-${i}`))}
          {hover && label(points[points.length - 1], hover, true, "live")}
        </g>;
      })()}
      <g aria-label={tr("Πυξίδα: βορράς επάνω", "Compass: north up")} pointerEvents="none"
        transform={`translate(${bounds.x + extent - extent / width * 52},${bounds.y + extent / width * 52}) scale(${extent / width})`}>
        <circle r="38" fill="white" fillOpacity="0.92" stroke="#94a3b8" />
        <path d="M 0,-21 L -7,10 L 0,5 L 7,10 Z" fill="#2563eb" />
        <path d="M 0,5 L -7,10 L 0,22 L 7,10 Z" fill="#94a3b8" />
        <g fill="#1e3a5f" fontSize="12" fontWeight="700" textAnchor="middle">
          <text y="-25">{tr("Β", "N")}</text><text y="33">{tr("Ν", "S")}</text>
          <text x="-28" y="4">{tr("Δ", "W")}</text><text x="28" y="4">{tr("Α", "E")}</text>
        </g>
      </g>
      {aligning && !drawing && onAlignPanels && roofPoints.map((p, i) => {
        const q = roofPoints[(i + 1) % roofPoints.length];
        return <line key={`align-${i}`} data-align-edge={i} x1={p.x} y1={-p.y} x2={q.x} y2={-q.y}
          stroke={hoverEdge === `align:${i}` ? "#f59e0b" : "#2563eb"} strokeOpacity={0.8}
          strokeWidth={extent / width * 8} style={{ cursor: "pointer" }} pointerEvents="stroke"
          onPointerEnter={() => setHoverEdge(`align:${i}`)} onPointerLeave={() => setHoverEdge(null)}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation();
            const result = alignmentDirection === "along" ? onAlignPanels([p, q], "along") : onAlignPanels([p, q]);
            if (result === false) { setError(turnError); return; }
            setError("");
            setAligning(false); setHoverEdge(null); }}>
          <title>{tr("Ευθυγράμμιση πάνελ με αυτή την ακμή", "Align panels with this edge")}</title>
        </line>;
      })}
      {objectHandle && <g data-object-rotation-handle={rotatingObjectId}
        data-obstacle-id={rotatingShared ? undefined : rotatingObject.id}
        data-shared-obstacle-id={rotatingShared ? rotatingObject.id : undefined}
        data-shared-plane={rotatingShared?.planeId}
        aria-label={tr("Χειριστήριο περιστροφής αντικειμένου", "Object rotation handle")} style={{ cursor: "grab" }}>
        <title>{tr("Σύρετε για περιστροφή · Enter ή Esc για τέλος", "Drag to rotate · Enter or Esc to finish")}</title>
        <line x1={rotatingObject.eastM} y1={-rotatingObject.northM} x2={objectHandle.x} y2={objectHandle.y}
          stroke="#d97706" strokeWidth={extent / width * 2} strokeDasharray={`${extent / width * 5} ${extent / width * 4}`} pointerEvents="none" />
        <circle cx={objectHandle.x} cy={objectHandle.y} r={extent / width * 12}
          fill="#fff7ed" stroke="#d97706" strokeWidth={extent / width * 2.5} />
        <text x={objectHandle.x} y={objectHandle.y} textAnchor="middle" dominantBaseline="central"
          fontSize={extent / width * 13} fill="#9a3412" pointerEvents="none">↻</text>
        <text x={objectHandle.x} y={objectHandle.y - extent / width * 20} textAnchor="middle"
          fontSize={extent / width * 13} fontWeight="700" fill="#9a3412" stroke="#fff7ed"
          strokeWidth={extent / width * 3} paintOrder="stroke" pointerEvents="none">{Number(objectBearing.toFixed(1))}°</text>
      </g>}
    </svg>
    {(aligning || rotatingPanels) && <Box sx={{ position: "absolute", left: 8, bottom: 8, zIndex: 2,
      width: "min(410px, calc(100% - 16px))", p: 1, bgcolor: "rgba(255,255,255,0.96)", border: "1px solid #cbd5e1",
      borderRadius: 1, boxShadow: 2 }}>
      {aligning && onAlignPanels && <Stack gap={0.6}>
        <Stack direction="row" gap={0.6} alignItems="center">
          <TextField select size="small" label={tr("Άξονας πάνελ", "Module axis")} value={alignmentDirection}
            onChange={(event) => setAlignmentDirection(event.target.value)} sx={{ minWidth: 220, flex: 1 }}>
            <MenuItem value="across">{tr("Κάθετα στην ακμή", "Across the edge")}</MenuItem>
            <MenuItem value="along">{tr("Κατά μήκος της ακμής", "Along the edge")}</MenuItem>
          </TextField>
          <Button size="small" onClick={() => setAligning(false)}>{tr("Ακύρωση", "Cancel")}</Button>
        </Stack>
        <Typography variant="caption">{tr("Επιλέξτε ακμή της ενεργής πλευράς. Η κλίση της επιφάνειας των πάνελ διατηρείται.",
          "Choose an edge of the active face. The module surface inclination is preserved.")}</Typography>
      </Stack>}
      {rotatingPanels && onRotatePanels && <Stack gap={0.6}>
        <Stack direction="row" gap={0.6} alignItems="center">
          <TextField size="small" type="number" label={tr("Άξονας πάνελ στην κάτοψη (°)", "Module axis on plan (°)")}
            value={panelAxisDraft} onChange={(event) => setPanelAxisDraft(event.target.value)}
            inputProps={{ min: 0, max: 360, step: 0.1 }} sx={{ minWidth: 190, flex: 1 }} />
          <Button size="small" variant="contained" disabled={invalidTurn || panelAxisDraft === "" || !Number.isFinite(Number(panelAxisDraft)) || Number(panelAxisDraft) < 0 || Number(panelAxisDraft) > 360}
            onClick={() => { if (commitPanelTurn(Number(panelAxisDraft) % 360)) setRotatingPanels(false); }}>
            {tr("Εφαρμογή", "Apply")}
          </Button>
          <Button size="small" onClick={() => setRotatingPanels(false)}>{tr("Ακύρωση", "Cancel")}</Button>
        </Stack>
        <Typography variant="caption">{tr("Σύρετε το κυκλικό χειριστήριο ή γράψτε ακριβή γωνία. Η συστοιχία περιστρέφεται μαζί.",
          "Drag the circular handle or enter an exact angle. The array rotates together.")}</Typography>
      </Stack>}
    </Box>}
    </Box>
    <Menu open={Boolean(menu)} onClose={() => setMenu(null)} anchorReference="anchorPosition"
      anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      slotProps={{ paper: { sx: { minWidth: 200 } } }} MenuListProps={{ dense: true }}>
      {menu?.pv && onEditPanels && <MenuItem selected={!moveArray} onClick={() => {
        finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null);
        setMoveArray(false); setSelectedPanel(menu.pvSlot); setMenu(null);
      }}>{tr("Μετακίνηση μόνο αυτού του πάνελ", "Move this module only")}</MenuItem>}
      {menu?.pvSlot && onEditPanels && <MenuItem sx={{ color: "error.main" }} onClick={() => {
        onEditPanels({ type: "remove", slots: [menu.pvSlot] }); setMenu(null);
      }}>{tr("Διαγραφή πάνελ", "Delete module")}</MenuItem>}
      {(menu?.pv || menu?.roof) && onEditPanels && <MenuItem onClick={() => {
        onEditPanels({ type: "add", row: menu.pvSlot ? Number(menu.pvSlot.split(":")[0]) : undefined,
          ...(menu.point ? { point: menu.point } : {}) }); setMenu(null);
      }}>{tr("Προσθήκη πάνελ", "Add module")}</MenuItem>}
      {menu?.missing && onEditPanels && <MenuItem onClick={() => {
        onEditPanels({ type: "restore", slot: menu.missing }); setMenu(null);
      }}>{tr("Επαναφορά πάνελ", "Restore module")}</MenuItem>}
      {onUndoPanelEdit && <MenuItem onClick={() => { onUndoPanelEdit(); setMenu(null); }}>
        {tr("Αναίρεση επεξεργασίας πάνελ", "Undo module edit")}</MenuItem>}
      {menu?.pv && <Divider />}
      {menu?.pv && onEditPanels && <MenuItem selected={moveArray} onClick={() => {
        finishOutline(); setAligning(false); setRotatingPanels(false); setRotatingObjectId(null);
        setMoveArray(true); setSelectedPanel(null); setMenu(null);
      }}>{tr("Μετακίνηση ολόκληρης συστοιχίας", "Move entire array")}</MenuItem>}
      {menu?.pv && onAlignPanels && roofPoints.length >= 3 && <MenuItem onClick={() => {
        finishOutline(); setAligning(!aligning); setRotatingPanels(false); setArrowsShown(new Set()); setMenu(null);
      }}>{aligning ? tr("Ακύρωση ευθυγράμμισης", "Cancel alignment") : tr("Ευθυγράμμιση συστοιχίας με ακμή", "Align array to edge")}</MenuItem>}
      {menu?.pv && onRotatePanels && <MenuItem onClick={() => { rotatingPanels ? setRotatingPanels(false) : showPanelRotation(); setMenu(null); }}>
        {rotatingPanels ? tr("Ακύρωση περιστροφής", "Cancel rotation") : tr("Περιστροφή ολόκληρης συστοιχίας", "Rotate entire array")}</MenuItem>}
      {menu?.roof && <MenuItem onClick={() => { toggleArrow("roof"); setMenu(null); }}>
        {arrowsShown.has("roof") ? tr("Απόκρυψη βέλους κλίσης", "Hide slope arrow") : tr("Βέλος κλίσης", "Slope arrow")}</MenuItem>}
      {menu?.roof && <MenuItem onClick={() => editOutline("roof")}>
        {outlineEditId === "roof" ? tr("Τέλος επεξεργασίας περιγράμματος", "Finish outline editing") : tr("Επεξεργασία περιγράμματος", "Edit outline")}</MenuItem>}
      {menu?.roof && <MenuItem onClick={() => startDrawing("pv-roof")}>{tr("Επανασχεδίαση περιγράμματος", "Redraw outline")}</MenuItem>}
      {menu?.roof && <Divider />}
      {menu?.roof && <MenuItem sx={{ color: "error.main" }} onClick={() => {
        // Same as the trash icon of the PV face in the objects table: the face
        // goes, and so does whatever stood on it.
        onChange({ ...value, roof: null, obstacles: obstacles.filter((o) => o.base !== "roof") });
        setSelected(null); setMenu(null);
      }}>{tr("Αφαίρεση πλευράς", "Remove face")}</MenuItem>}
      {menu?.obstacle?.kind === "roof" && <MenuItem onClick={() => { toggleArrow(menu.obstacle.id); setMenu(null); }}>
        {arrowsShown.has(menu.obstacle.id) ? tr("Απόκρυψη βέλους κλίσης", "Hide slope arrow") : tr("Βέλος κλίσης", "Slope arrow")}</MenuItem>}
      {menu?.obstacle?.kind === "roof" && <MenuItem onClick={() => editOutline(menu.obstacle.id)}>
        {outlineEditId === menu.obstacle.id ? tr("Τέλος επεξεργασίας περιγράμματος", "Finish outline editing") : tr("Επεξεργασία περιγράμματος", "Edit outline")}</MenuItem>}
      {menu?.obstacle?.kind === "roof" && onFacePv && <MenuItem onClick={() => { onFacePv({ obstacle: menu.obstacle }, true); setMenu(null); }}>
        {tr("Φ/Β σε αυτή την πλευρά", "PV on this face")}</MenuItem>}
      {canRotateObstacle(menu?.obstacle) && <MenuItem onClick={() => showObjectRotation(menu.obstacle.id)}>
        {tr("Περιστροφή αντικειμένου", "Rotate object")}</MenuItem>}
      {menu?.obstacle && <Divider />}
      {menu?.obstacle && <MenuItem onClick={() => { onChange({ ...value, obstacles: obstacles.filter((o) => o.id !== menu.obstacle.id) }); setSelected(null); setMenu(null); }}
        sx={{ color: "error.main" }}>{tr("Διαγραφή", "Delete")}</MenuItem>}
      {menu?.shared?.obstacle?.kind === "roof" && <MenuItem onClick={() => { toggleArrow(`shared:${menu.shared.planeId}:${menu.shared.obstacle.id}`); setMenu(null); }}>
        {arrowsShown.has(`shared:${menu.shared.planeId}:${menu.shared.obstacle.id}`) ? tr("Απόκρυψη βέλους κλίσης", "Hide slope arrow") : tr("Βέλος κλίσης", "Slope arrow")}</MenuItem>}
      {menu?.shared?.obstacle?.kind === "roof" && onSharedObstacleChange && <MenuItem onClick={() => editOutline(`shared:${menu.shared.planeId}:${menu.shared.obstacle.id}`)}>
        {outlineEditId === `shared:${menu.shared.planeId}:${menu.shared.obstacle.id}` ? tr("Τέλος επεξεργασίας περιγράμματος", "Finish outline editing") : tr("Επεξεργασία περιγράμματος", "Edit outline")}</MenuItem>}
      {onSharedObstacleChange && canRotateObstacle(menu?.shared?.obstacle) && <MenuItem
        onClick={() => showObjectRotation(`shared:${menu.shared.planeId}:${menu.shared.obstacle.id}`)}>
        {tr("Περιστροφή αντικειμένου", "Rotate object")}</MenuItem>}
      {menu?.shared && onSharedObstacleRemove && <MenuItem sx={{ color: "error.main" }} onClick={() => {
        onSharedObstacleRemove(menu.shared.planeId, menu.shared.obstacle.id); setSelected(null); setMenu(null);
      }}>{tr("Διαγραφή", "Delete")}</MenuItem>}
      {menu?.plane && onSelectPlane && <MenuItem onClick={() => { onSelectPlane(menu.plane.planeId); setMenu(null); }}>
        {tr("Επεξεργασία αυτής της πλευράς", "Edit this face")}</MenuItem>}
      {menu?.plane && onFacePv && !menu.plane.primary && <MenuItem onClick={() => { onFacePv({ planeId: menu.plane.planeId }, false); setMenu(null); }}>
        {tr("Αφαίρεση Φ/Β από αυτή την πλευρά", "Remove PV from this face")}</MenuItem>}
    </Menu>
    {satellite && <Typography variant="caption">{process.env.REACT_APP_MAP_SATELLITE_ATTRIBUTION?.replace(/<[^>]*>/g, "") || "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community"}</Typography>}
    {satellite && tileError && <Alert severity="info">{tr("Δεν φορτώθηκε μέρος της δορυφορικής εικόνας. Δοκιμάστε σμίκρυνση ή εισαγωγή κάτοψης.", "Some imagery could not load. Try zooming out or uploading a plan.")}</Alert>}
    {drawing && points.length >= 3 && !validPolygon(points) && <Alert severity="warning">{tr("Το περίγραμμα είναι εκφυλισμένο ή έχει τεμνόμενες ακμές.", "The footprint is degenerate or self-intersecting.")}</Alert>}
  </Box>;
}
